import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock3,
  Eye,
  FileText,
  GripVertical,
  Lock,
  MoveUp,
  MoveDown,
  Plus,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  Trash2,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, ui } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";
import { hrmsGet } from "../../../api/api";
import i18n from "../../../i18n";
import { CRM_ONBOARDING_ADD_PATH } from "../../crm/routes";
import { customerService } from "../../../services/customerService";
import { searchComplaintsByStatus } from "../../../services/fsmService";
import {
  createQuotation,
  downloadQuotationPdf,
  fetchCatalogProductById,
  fetchMakesFromProductCatalog,
  fetchPartNumbersByMake,
  getQuotationById,
  getQuotations,
  mapQuotationTypeForApi,
  reviewQuotation,
  reviseQuotation,
  submitQuotation,
  updateQuotationStatus,
  updateQuotationWorkingSheet,
} from "../../../services/quotationService";
import { fetchRoles } from "../../../services/rolesPermissionsService";
import {
  createApprovalRule,
  createPmcConfig,
  createTerm,
  deletePmcConfig,
  deleteApprovalRule,
  deleteTerm,
  getApprovalRules,
  getNetPriceRule,
  getPmcConfigs,
  getTerms,
  updatePmcConfig,
  updateTerm,
  updateNetPriceRule,
} from "../../../services/salesConfigService";
import sessionDB from "../../../utils/sessionDB";

const CUSTOMER_STAGE_STATUSES = ["Approved", "Sent to Customer", "Under Negotiation", "Accepted", "Rejected"];
const SALES_TYPES = ["Supply", "SITC", "SERVICE"];
const STATUS_OPTIONS = ["Draft", "Pending Approval", "Approved", "Sent to Customer", "Under Negotiation", "Accepted", "Rejected"];
const STEP_STATUSES = ["Draft", "Pending Approval", "Approved", "Sent to Customer", "Under Negotiation"];

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function localizedFieldProps() {
  const language = ["hi", "te", "kn"].includes(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : "en";

  return {
    lang: language,
    autoCapitalize: "off",
    spellCheck: false,
  };
}

function currentIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatInputNumber(value) {
  const rounded = Number(value || 0);
  if (!Number.isFinite(rounded)) return "";
  return Number.isInteger(rounded) ? String(rounded) : String(Number(rounded.toFixed(2)));
}

function translateQuotationType(type) {
  if (type === "Supply") return sq("typeSupply");
  if (type === "SITC") return sq("typeSitc");
  if (type === "SERVICE" || type === "Service") return sq("typeService");
  return type;
}

function translateQuotationStatus(status) {
  const map = {
    Draft: "statusDraft",
    "Pending Approval": "statusPendingApproval",
    Approved: "statusApproved",
    "Sent to Customer": "statusSentToCustomer",
    "Under Negotiation": "statusUnderNegotiation",
    Accepted: "statusAccepted",
    Rejected: "statusRejected",
    Expired: "statusExpired",
  };
  return map[status] ? sq(map[status]) : status;
}

function normalizeCustomerOption(customer = {}) {
  return {
    id: customer.customerId ?? customer.id ?? "",
    name: customer.companyName ?? customer.company_name ?? customer.name ?? "",
    raw: customer,
  };
}

function normalizeLocationOption(location = {}) {
  return {
    id: location.locationId ?? location.id ?? "",
    name:
      location.locationName ??
      location.name ??
      location.addressLine1 ??
      location.address ??
      "",
    customerId:
      location.customerId ??
      location.customer_id ??
      location.customerInfoId ??
      location.customerInfo?.customerId ??
      "",
    raw: location,
  };
}

function normalizeContactOption(contact = {}) {
  const firstName = contact.firstName ?? "";
  const lastName = contact.lastName ?? "";
  return {
    id: contact.contactId ?? contact.id ?? "",
    name:
      contact.contactName ??
      contact.name ??
      `${firstName} ${lastName}`.trim(),
    phone:
      contact.phone ??
      contact.phoneNumber ??
      contact.mobile ??
      contact.contactPhone ??
      "",
    email:
      contact.email ??
      contact.contactEmail ??
      "",
    customerId:
      contact.customerId ??
      contact.customer_id ??
      contact.customerInfoId ??
      contact.customerInfo?.customerId ??
      "",
    raw: contact,
  };
}

function normalizeEmployeeOption(employee = {}) {
  const firstName = employee.firstName ?? employee.first_Name ?? "";
  const lastName = employee.lastName ?? employee.last_Name ?? "";
  const fullName =
    employee.fullName ??
    employee.employeeName ??
    employee.name ??
    [firstName, lastName].filter(Boolean).join(" ");

  return {
    id: employee.empId ?? employee.emp_id ?? employee.employeeId ?? employee.id ?? "",
    name: String(fullName || "").trim(),
    raw: employee,
  };
}

function parseCurrency(value) {
  const normalized = String(value ?? "").replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return `Rs${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isExpired(expiry) {
  return Boolean(expiry) && expiry < currentIsoDate();
}

function statusTone(status) {
  switch (status) {
    case "Draft":
      return "draft";
    case "Pending Approval":
      return "pending";
    case "Approved":
      return "approved";
    case "Sent to Customer":
      return "sent";
    case "Under Negotiation":
      return "negotiation";
    case "Accepted":
      return "accepted";
    case "Rejected":
      return "rejected";
    default:
      return "draft";
  }
}

function normalizeStatusLabel(status) {
  const normalized = String(status || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();

  if (
    normalized === "pending approval" ||
    normalized === "pending l1 approval" ||
    normalized === "pending l2 approval" ||
    normalized === "pending l3 approval" ||
    (normalized.startsWith("pending ") && normalized.includes("approval"))
  ) {
    return "Pending Approval";
  }

  switch (normalized) {
    case "approved":
      return "Approved";
    case "sent to customer":
      return "Sent to Customer";
    case "under negotiation":
      return "Under Negotiation";
    case "accepted":
      return "Accepted";
    case "rejected":
      return "Rejected";
    case "draft":
      return "Draft";
    default:
      return "Draft";
  }
}

function isAwaitingApprovalNote(note) {
  const text = String(note || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("submitted and locked") ||
    (text.includes("awaiting") && text.includes("approval"))
  );
}

function buildAwaitingApprovalMessage(item = {}) {
  const roleName = String(item.approverRoleName || "Sales Manager").trim() || "Sales Manager";
  const level = String(item.requiredApprovalLevel || "LEVEL_1")
    .replace(/[_-]+/g, " ")
    .trim();
  const levelLabel = /level\s*2/i.test(level)
    ? "Level 2"
    : /level\s*3/i.test(level)
      ? "Level 3"
      : "Level 1";
  return `Submitted and locked — awaiting ${levelLabel} approval from ${roleName} (Default)`;
}

function resolveQuotationNotes(item = {}, status) {
  const rawNote = String(item.managerNote || item.revisionNote || "").trim();

  if (status === "Pending Approval") {
    return {
      submissionNote: rawNote || buildAwaitingApprovalMessage(item),
      revisionNote: "",
    };
  }

  if (status === "Draft" && rawNote && !isAwaitingApprovalNote(rawNote)) {
    return {
      submissionNote: "",
      revisionNote: rawNote,
    };
  }

  return {
    submissionNote: "",
    revisionNote: "",
  };
}

function normalizeQuotationType(type) {
  const normalized = String(type || "").trim().toUpperCase();
  if (normalized.includes("INSTALL") || normalized === "SITC") return "SITC";
  if (normalized === "SERVICE") return "SERVICE";
  return "Supply";
}

function extractApiCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.quotations)) return payload.quotations;
  if (Array.isArray(payload?.terms)) return payload.terms;
  if (Array.isArray(payload?.standardTerms)) return payload.standardTerms;
  if (Array.isArray(payload?.pmcConfigs)) return payload.pmcConfigs;
  if (Array.isArray(payload?.pmcCostHeads)) return payload.pmcCostHeads;
  if (Array.isArray(payload?.heads)) return payload.heads;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.terms)) return payload.data.terms;
  if (Array.isArray(payload?.data?.standardTerms)) return payload.data.standardTerms;
  if (Array.isArray(payload?.data?.pmcConfigs)) return payload.data.pmcConfigs;
  if (Array.isArray(payload?.data?.pmcCostHeads)) return payload.data.pmcCostHeads;
  if (Array.isArray(payload?.data?.heads)) return payload.data.heads;
  if (Array.isArray(payload?.approvalRules)) return payload.approvalRules;
  if (Array.isArray(payload?.data?.approvalRules)) return payload.data.approvalRules;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data) && payload.data.id != null) {
    return [payload.data];
  }
  if (payload?.id != null && (payload.sellingPriceMin != null || payload.approvalLevel != null)) {
    return [payload];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function extractNumericId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseContentDispositionFilename(headerValue, fallback = "quotation.pdf") {
  if (!headerValue) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const basicMatch = /filename="?([^";]+)"?/i.exec(headerValue);
  return basicMatch?.[1] || fallback;
}

function triggerBrowserDownload(blob, filename) {
  if (typeof window === "undefined") return;
  const href = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(href);
}

function extractObjectPayload(payload) {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) return payload;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) return payload.data;
  return null;
}

function mapApiNetPriceRuleToUi(raw = {}) {
  const source =
    extractObjectPayload(raw) ||
    (Array.isArray(raw) ? extractObjectPayload(raw[0]) : null) ||
    (Array.isArray(raw?.content) ? extractObjectPayload(raw.content[0]) : null) ||
    {};

  const percentage = firstDefined(
    source.netPricePercentage,
    source.net_price_percentage,
    source.percentage,
    source.netPricePct,
    source.value
  );

  return {
    id: source.id ?? "",
    tenantId: String(source.tenantId ?? source.tenant_id ?? ""),
    netPricePercentage:
      percentage === undefined || percentage === null || percentage === ""
        ? ""
        : String(percentage),
  };
}

function firstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function hasQuotationDetailPayload(payload) {
  if (!payload) return false;
  if (Array.isArray(payload)) return payload.length > 0;
  if (Array.isArray(payload?.data)) return payload.data.length > 0;
  if (extractObjectPayload(payload)) return true;
  return false;
}

function parseApiBoolean(value, defaultValue = true) {
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === undefined || value === null) return defaultValue;
  return Boolean(value);
}

function mapApiApprovalRuleToUi(rule = {}, index = 0) {
  const approvalLevel = String(rule.approvalLevel || "").trim();
  const autoApprove =
    rule.autoApprove != null
      ? parseApiBoolean(rule.autoApprove, false)
      : rule.auto_approve != null
        ? parseApiBoolean(rule.auto_approve, false)
        : rule.needsApproval != null
          ? !parseApiBoolean(rule.needsApproval, true)
          : !approvalLevel ||
            approvalLevel.toUpperCase() === "AUTO_APPROVE" ||
            approvalLevel.toUpperCase() === "AUTO-APPROVE";

  return {
    id: rule.id ?? `rule-${index + 1}`,
    sellingPriceMin: formatRuleEditableValue(rule.sellingPriceMin),
    sellingPriceMax: formatRuleEditableValue(rule.sellingPriceMax),
    marginMin: formatRuleEditableValue(rule.marginMin),
    marginMax: formatRuleEditableValue(rule.marginMax),
    approvalLevel,
    displayOrder: Number(rule.displayOrder ?? index + 1),
    isActive: parseApiBoolean(rule.isActive, true),
    autoApprove,
  };
}

function formatRuleEditableValue(value) {
  const parsed = Number(value);
  if (value === "" || value === null || value === undefined) return "";
  if (Number.isFinite(parsed) && parsed === 0) return "";
  return String(value);
}

function parseRuleEditableValue(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || trimmed.toLowerCase() === "any") return "";
  return trimmed;
}

function buildApprovalRulePayload(rule = {}) {
  const autoApprove = Boolean(rule.autoApprove);
  return {
    id: Number(rule.id) || 0,
    sellingPriceMin: Number(parseRuleEditableValue(rule.sellingPriceMin)) || 0,
    sellingPriceMax: Number(parseRuleEditableValue(rule.sellingPriceMax)) || 0,
    marginMin: Number(parseRuleEditableValue(rule.marginMin)) || 0,
    marginMax: Number(parseRuleEditableValue(rule.marginMax)) || 0,
    approvalLevel: autoApprove ? "" : String(rule.approvalLevel || "").trim(),
    displayOrder: Number(rule.displayOrder) || 1,
    isActive: Boolean(rule.isActive),
    autoApprove,
  };
}

function extractApiApprovalRules(payload) {
  if (!payload) return [];
  const collection = extractApiCollection(payload);
  if (collection.length) {
    return collection.map((rule, index) => mapApiApprovalRuleToUi(rule, index));
  }
  const single = extractObjectPayload(payload);
  if (single && single.id != null) {
    return [mapApiApprovalRuleToUi(single)];
  }
  return [];
}

function dedupeApprovalRules(rules = []) {
  const grouped = new Map();

  for (const rule of rules) {
    const key = [
      rule.displayOrder,
      rule.sellingPriceMin,
      rule.sellingPriceMax,
      rule.marginMin,
      rule.marginMax,
      rule.approvalLevel,
      rule.autoApprove ? "1" : "0",
      rule.isActive ? "1" : "0",
    ].join("|");
    const existing = grouped.get(key);
    const ruleId = Number(rule.id) || 0;
    const existingId = Number(existing?.id) || 0;
    if (!existing || ruleId >= existingId) {
      grouped.set(key, rule);
    }
  }

  return sortApprovalRules(Array.from(grouped.values()));
}

function mapApiApprovalRuleResponse(response) {
  const saved =
    extractObjectPayload(response?.data) ||
    extractObjectPayload(response) ||
    null;
  return saved ? mapApiApprovalRuleToUi(saved) : null;
}

async function persistApprovalRule(rule, { previousId = 0 } = {}) {
  const payload = buildApprovalRulePayload({ ...rule, id: 0 });
  const response = await createApprovalRule(payload);
  const savedRule = mapApiApprovalRuleResponse(response);
  const resolvedPreviousId = Number(previousId) || 0;
  const savedId = Number(savedRule?.id) || 0;

  if (resolvedPreviousId > 0 && savedId > 0 && resolvedPreviousId !== savedId) {
    try {
      await deleteApprovalRule(resolvedPreviousId);
    } catch {
      // New rule was saved; stale duplicate cleanup can be retried on refresh.
    }
  }

  return savedRule;
}

function resolveDefaultApproverLabel(options = []) {
  const salesManager = options.find((option) =>
    String(option || "").trim().toLowerCase().includes("sales manager")
  );
  return salesManager || options[0] || i18n.t("salesQuotation.salesManager");
}

function createEmptyApprovalRule(order = 1, approverOptions = []) {
  return {
    id: `draft-${Date.now()}`,
    sellingPriceMin: "",
    sellingPriceMax: "",
    marginMin: "",
    marginMax: "",
    approvalLevel: approverOptions[0] || "",
    displayOrder: order,
    isActive: true,
    autoApprove: false,
    isDraft: true,
  };
}

function sortApprovalRules(rules = []) {
  return [...rules]
    .sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder))
    .map((rule, index) => ({ ...rule, displayOrder: index + 1 }));
}

function quotationIsEditable(status) {
  return String(status || "").trim().toLowerCase() === "draft";
}

function inferPmcCategory(name = "") {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  return trimmed.split("(")[0].trim();
}

function buildPmcHeadPresentation(headName = "", category = "") {
  const resolvedCategory = String(category || inferPmcCategory(headName)).trim();
  const normalizedCategory = resolvedCategory.toLowerCase();
  const isManpower = normalizedCategory.includes("manpower");

  return {
    category: resolvedCategory,
    description: isManpower
      ? "Roles for this head. Nos, months and CTC are entered per-quotation in Cost + Pricing."
      : "Line items for this head. Quantities and rates are entered per-quotation in Cost + Pricing.",
  };
}

function normalizePmcColumnsInput(value, fallback = []) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const objectValues = Object.values(value).filter(Boolean);
    if (objectValues.length) return objectValues;
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return normalizePmcColumnsInput(parsed, fallback);
    } catch {
      if (value.includes("\n")) {
        return normalizePmcColumnsInput(value.split("\n").map((line) => line.trim()).filter(Boolean), fallback);
      }
      if (value.includes(",")) {
        return normalizePmcColumnsInput(value.split(",").map((part) => part.trim()).filter(Boolean), fallback);
      }
    }
  }

  if (Array.isArray(value) && value.length) return value.filter(Boolean);
  return Array.isArray(fallback) ? fallback.filter(Boolean) : [];
}

function resolvePmcColumnLabel(column, index = 0) {
  if (typeof column === "string") return column.trim();
  if (column && typeof column === "object") {
    return String(
      column.label ??
      column.name ??
      column.title ??
      column.header ??
      column.columnName ??
      column.displayName ??
      column.fieldLabel ??
      column.columnLabel ??
      column.text ??
      column.key ??
      ""
    ).trim() || `Column ${index + 1}`;
  }
  return `Column ${index + 1}`;
}

function resolvePmcColumnFieldKey(column, index = 0) {
  if (column && typeof column === "object") {
    const explicitKey = String(
      column.fieldKey ??
      column.field ??
      column.dataKey ??
      column.binding ??
      column.key ??
      ""
    ).trim();
    if (explicitKey && !/^(label|name|title|header|columnName|displayName)$/i.test(explicitKey)) {
      return explicitKey;
    }
  }
  return inferPmcColumnFieldKey(resolvePmcColumnLabel(column, index), index);
}

function normalizePmcColumnLabels(value) {
  return normalizePmcColumnsInput(value, [])
    .map((column, index) => resolvePmcColumnLabel(column, index))
    .filter(Boolean);
}

function normalizePmcRowLabels(value) {
  if (Array.isArray(value)) {
    return value.map((row) => {
      if (typeof row === "string") return row;
      return String(
        row?.description ??
        row?.name ??
        row?.label ??
        row?.item ??
        row?.title ??
        row?.rowName ??
        row?.itemName ??
        row?.value ??
        row?.text ??
        ""
      ).trim();
    });
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizePmcRowLabels(parsed);
    } catch {
      /* plain newline / comma text */
    }
    if (value.includes("\n")) {
      return value.split("\n").map((line) => line.trim()).filter(Boolean);
    }
    if (value.includes(",")) {
      return value.split(",").map((part) => part.trim()).filter(Boolean);
    }
    return [value.trim()];
  }
  return [];
}

function pickPmcColumnsFromApi(head = {}) {
  const direct = normalizePmcColumnsInput(
    head.columns ??
    head.columnLabels ??
    head.breakdownColumns ??
    head.headers ??
    head.columnDefinitions ??
    head.columnDefs ??
    head.breakdownStructure?.columns ??
    head.breakdown?.columns ??
    head.config?.columns,
    []
  );
  if (direct.length) return direct;

  if (head.breakdownStructure && typeof head.breakdownStructure === "object") {
    const nested = pickPmcColumnsFromApi(head.breakdownStructure);
    if (nested.length) return nested;
  }

  for (const [key, value] of Object.entries(head || {})) {
    if (
      !/(column|header|breakdown)/i.test(key) ||
      ["defaultRows", "rows", "items", "lineItems"].includes(key) ||
      !Array.isArray(value) ||
      !value.length
    ) {
      continue;
    }
    const columns = normalizePmcColumnsInput(value, []);
    if (!columns.length) continue;
    const looksLikeColumns = columns.every((column) => {
      if (typeof column === "string") return Boolean(column.trim());
      return Boolean(
        column?.label ||
        column?.name ||
        column?.title ||
        column?.header ||
        column?.columnName ||
        column?.displayName ||
        column?.fieldLabel ||
        column?.columnLabel
      );
    });
    if (looksLikeColumns) return columns;
  }

  return [];
}

function mapPmcConfigStatus(head = {}) {
  if (head.isActive === false || head.active === false) return "Inactive";
  if (String(head.status || "").trim().toLowerCase() === "inactive") return "Inactive";
  return "Active";
}

function pickPmcRowsFromApi(head = {}) {
  const explicit = normalizePmcRowLabels(
    head.defaultRows ??
    head.rows ??
    head.breakdownRows ??
    head.items ??
    head.defaultItems ??
    head.rowLabels ??
    head.breakdownItems ??
    head.lineItems ??
    head.defaultRowLabels ??
    head.rowNames ??
    head.breakdown ??
    head.configRows
  ).filter(Boolean);

  if (explicit.length) return explicit;

  // Fallback: detect any array of row-like values on the API object.
  for (const [key, value] of Object.entries(head || {})) {
    if (
      ["columns", "columnLabels", "breakdownColumns", "headers"].includes(key) ||
      !Array.isArray(value) ||
      !value.length
    ) {
      continue;
    }
    const rows = normalizePmcRowLabels(value).filter(Boolean);
    if (rows.length) return rows;
  }

  return [];
}

function mapApiPmcConfigToUi(head = {}, index = 0) {
  const presentation = buildPmcHeadPresentation(head.headName || head.name, head.category);
  const defaultRows = pickPmcRowsFromApi(head);
  const rawColumns = pickPmcColumnsFromApi(head);
  const columns = normalizePmcColumnLabels(rawColumns);

  return {
    id: head.id ?? head.pmcConfigId ?? `pmc-${index + 1}`,
    category: head.category || presentation.category,
    name: head.headName || head.name || `PMC Head ${index + 1}`,
    status: mapPmcConfigStatus(head),
    order: Number(head.displayOrder ?? head.order ?? index + 1),
    columns,
    rawColumns,
    defaultRows: defaultRows.length ? defaultRows : [""],
    description: head.description || presentation.description,
    dirty: false,
  };
}

function extractApiPmcConfigs(payload) {
  const direct = extractApiCollection(payload);
  if (direct.length) return direct.map(mapApiPmcConfigToUi);

  const nestedKeys = ["pmcConfigs", "configs", "pmcCostHeads", "heads", "pmcHeadList"];
  for (const key of nestedKeys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key].map(mapApiPmcConfigToUi);
    }
    if (Array.isArray(payload?.data?.[key])) {
      return payload.data[key].map(mapApiPmcConfigToUi);
    }
  }

  const single = extractObjectPayload(payload);
  if (single && (single.headName || single.name || single.id != null) && !Array.isArray(single)) {
    return [mapApiPmcConfigToUi(single)];
  }

  return [];
}

function buildPmcConfigPayload(head = {}) {
  const presentation = buildPmcHeadPresentation(head.name, head.category);
  const columns = normalizePmcColumnLabels(head.columns);
  const defaultRows = normalizePmcRowLabels(head.defaultRows).filter(Boolean);

  return {
    id: Number(head.id) || 0,
    category: String(head.category || presentation.category || inferPmcCategory(head.name) || "").trim(),
    headName: String(head.name || "").trim(),
    isActive: head.status !== "Inactive",
    columns,
    defaultRows,
  };
}

function applySavedRowsToHeads(heads = [], savedRowsById = {}) {
  return heads.map((head) => {
    const key = String(head.id);
    const savedRows = normalizePmcRowLabels(savedRowsById[key]).filter(Boolean);
    const apiRows = normalizePmcRowLabels(head.defaultRows).filter(Boolean);
    if (apiRows.length) {
      return { ...head, defaultRows: apiRows, dirty: false };
    }
    if (savedRows.length) {
      return { ...head, defaultRows: savedRows, dirty: false };
    }
    return { ...head, dirty: false };
  });
}

function mapApiTermToUi(term = {}, index = 0) {
  const numericId = extractNumericId(term.id ?? term.termId ?? term.term_id);

  return {
    id: numericId ?? `term-${index + 1}`,
    title: term.title || term.name || `Term ${index + 1}`,
    text: term.description || term.text || "",
    order: Number(term.displayOrder ?? term.order ?? index + 1),
    status: term.isActive === false || term.status === "Inactive" ? "Inactive" : "Active",
  };
}

function sortTermsForUi(terms = []) {
  return terms
    .sort((a, b) => Number(a.order) - Number(b.order))
    .map((item, index) => ({ ...item, order: index + 1 }));
}

function extractApiTerms(payload) {
  return extractApiCollection(payload).map(mapApiTermToUi);
}

function inferPmcColumnFieldKey(label = "", index = 0) {
  const normalized = String(label || "").trim().toLowerCase();
  if (/^(description|item|role|particulars)/.test(normalized)) return "description";
  if (/^(amount|total)$/.test(normalized)) return "totalCost";
  if (/boq|bill of qty/.test(normalized)) return "boqQty";
  if (/cons\.?\s*qty|consumables?\s*qty/.test(normalized)) return "consQty";
  if (/^nos$/.test(normalized)) return "nos";
  if (/month/.test(normalized)) return "months";
  if (/^ctc$/.test(normalized)) return "rate";
  if (/unit\s*rate|unitrate/.test(normalized)) return "unitRate";
  if (/^rate$/.test(normalized)) return "rate";
  if (/^qty$|^quantity$/.test(normalized)) return "qty";
  return `col${index}`;
}

function buildPmcColumnDefinitions(columns = []) {
  const normalizedInput = normalizePmcColumnsInput(columns);
  return normalizedInput.map((column, index) => {
    const label = resolvePmcColumnLabel(column, index);
    const key = resolvePmcColumnFieldKey(column, index);
    const numeric = key !== "description";
    return {
      id: `pmc-col-${index}`,
      reactKey: `pmc-col-${index}`,
      key,
      label,
      computed: key === "totalCost",
      readonly: key === "description" || key === "totalCost",
      align: key === "description" ? "left" : "right",
      numeric,
    };
  });
}

function getPmcColumnKeys(columns = []) {
  return buildPmcColumnDefinitions(columns).map((column) => column.key);
}

function computePmcRowTotal(entry = {}, columns = []) {
  const columnKeys = getPmcColumnKeys(columns);

  const nos = parseCurrency(entry.nos);
  const months = parseCurrency(entry.months);
  const rate = parseCurrency(entry.rate ?? entry.unitRate ?? entry.ctc);
  const boqQty = parseCurrency(entry.boqQty);
  const consQty = parseCurrency(entry.consQty ?? entry.qty);
  const qty = parseCurrency(entry.qty);

  if (columnKeys.includes("nos") && columnKeys.includes("months") && (columnKeys.includes("rate") || columnKeys.includes("unitRate"))) {
    return nos * months * rate;
  }
  if (columnKeys.includes("boqQty") && columnKeys.includes("consQty") && (columnKeys.includes("rate") || columnKeys.includes("unitRate"))) {
    return boqQty * consQty * rate;
  }
  if (columnKeys.includes("boqQty") && (columnKeys.includes("rate") || columnKeys.includes("unitRate"))) {
    const multiplier = consQty || qty || 1;
    return boqQty * rate * multiplier;
  }
  const resolvedQty = qty || nos || boqQty || consQty;
  if (resolvedQty && rate) {
    return resolvedQty * rate;
  }

  return Number(entry.totalCost ?? entry.amount ?? entry.total ?? 0) || 0;
}

function normalizePmcEntry(entry = {}, index = 0) {
  const columnSource = entry.rawColumns?.length ? entry.rawColumns : entry.columns;
  const columns = Array.isArray(columnSource) && columnSource.length
    ? normalizePmcColumnLabels(columnSource)
    : [];
  const normalized = {
    id: entry.id ?? `pmc-entry-${index + 1}`,
    pmcConfigId: entry.pmcConfigId ?? entry.pmc_config_id ?? null,
    category: entry.category || inferPmcCategory(entry.headName || entry.description || ""),
    headName: entry.headName || entry.description || `PMC Head ${index + 1}`,
    description: entry.description || entry.item || entry.roleName || "",
    columns,
    rawColumns: Array.isArray(columnSource) ? columnSource : [],
    configDescription: entry.configDescription || "",
    boqQty: String(entry.boqQty ?? ""),
    consQty: String(entry.consQty ?? ""),
    qty: String(entry.qty ?? entry.quantity ?? ""),
    nos: String(entry.nos ?? ""),
    months: String(entry.months ?? ""),
    rate: String(entry.rate ?? entry.ctc ?? entry.unitRate ?? ""),
    unitRate: String(entry.unitRate ?? entry.rate ?? ""),
    totalCost: Number(entry.totalCost ?? entry.amount ?? entry.total ?? 0) || 0,
  };
  normalized.totalCost = computePmcRowTotal(normalized, normalized.rawColumns.length ? normalized.rawColumns : columns);
  return normalized;
}

function createPmcEntryFromConfig(config = {}, rowLabel = "", rowIndex = 0, savedEntry = null) {
  const rawColumns = config.rawColumns?.length ? config.rawColumns : pickPmcColumnsFromApi(config);
  const columns = normalizePmcColumnLabels(rawColumns);
  const entry = {
    id: `pmc-${config.id}-${rowIndex}`,
    pmcConfigId: config.id,
    category: config.category,
    headName: config.name,
    description: rowLabel,
    columns,
    rawColumns,
    configDescription: config.description || "",
    boqQty: "",
    consQty: "",
    qty: "",
    nos: "",
    months: "",
    rate: "",
    unitRate: "",
    totalCost: 0,
  };

  if (savedEntry) {
    if (savedEntry.id != null && !String(savedEntry.id).startsWith("pmc-")) {
      entry.id = savedEntry.id;
    }
    entry.boqQty = String(savedEntry.boqQty ?? "");
    entry.consQty = String(savedEntry.consQty ?? "");
    entry.qty = String(savedEntry.qty ?? savedEntry.quantity ?? "");
    entry.nos = String(savedEntry.nos ?? "");
    entry.months = String(savedEntry.months ?? "");
    entry.rate = String(savedEntry.rate ?? savedEntry.unitRate ?? savedEntry.ctc ?? "");
    entry.unitRate = String(savedEntry.unitRate ?? savedEntry.rate ?? "");
  }

  entry.totalCost = computePmcRowTotal(entry, rawColumns.length ? rawColumns : columns);
  return entry;
}

function findSavedPmcEntry(savedEntries = [], config = {}, rowLabel = "", rowIndex = 0) {
  const configId = String(config.id ?? "");
  const scoped = savedEntries.filter((entry) => String(entry.pmcConfigId ?? entry.pmc_config_id ?? "") === configId);
  const label = String(rowLabel || "").trim().toLowerCase();

  if (label) {
    const byLabel = scoped.find((entry) => {
      const candidate = String(entry.description || entry.item || entry.roleName || "").trim().toLowerCase();
      return candidate && candidate === label;
    });
    if (byLabel) return byLabel;
  }

  return scoped[rowIndex] || null;
}

function mergePmcEntriesWithConfigs(configs = [], savedEntries = []) {
  const activeConfigs = (Array.isArray(configs) ? configs : []).filter(
    (config) => config.status === "Active"
  );
  if (!activeConfigs.length) return [];

  const merged = [];
  activeConfigs
    .sort((a, b) => Number(a.order) - Number(b.order))
    .forEach((config) => {
      const rowLabels = normalizePmcRowLabels(config.defaultRows).filter(Boolean);
      const labels = rowLabels.length ? rowLabels : [""];

      labels.forEach((label, rowIndex) => {
        const savedEntry = findSavedPmcEntry(savedEntries, config, label, rowIndex);
        merged.push(createPmcEntryFromConfig(config, label, rowIndex, savedEntry));
      });
    });

  return merged;
}

function patchPmcEntryValue(entries = [], entryId, fieldKey, value) {
  const targetId = String(entryId);
  return entries.map((entry) => {
    if (String(entry.id) !== targetId) return entry;
    const next = { ...entry, [fieldKey]: value };
    if (fieldKey === "rate" || fieldKey === "unitRate") {
      next.rate = value;
      next.unitRate = value;
    }
    next.totalCost = computePmcRowTotal(next, entry.rawColumns?.length ? entry.rawColumns : entry.columns || []);
    return next;
  });
}

async function resolvePmcEntriesForQuotation(_quotationType, savedEntries = []) {
  const response = await getPmcConfigs();
  const configs = extractApiPmcConfigs(response?.data ?? response);
  const activeConfigs = configs.filter((config) => config.status === "Active");
  if (!activeConfigs.length) {
    return { entries: [], hasPmcConfigs: false };
  }

  return {
    entries: mergePmcEntriesWithConfigs(activeConfigs, savedEntries),
    hasPmcConfigs: true,
  };
}

function groupPmcEntriesForDisplay(entries = []) {
  const grouped = new Map();

  entries.forEach((entry, index) => {
    const normalized = normalizePmcEntry(entry, index);
    const columnSource = normalized.rawColumns?.length ? normalized.rawColumns : normalized.columns;
    const columns = columnSource?.length ? buildPmcColumnDefinitions(columnSource) : [];

    const groupKey = String(normalized.pmcConfigId ?? normalized.id ?? `group-${index}`);
    const row = {
      ...normalized,
      totalCost: computePmcRowTotal(normalized, columnSource?.length ? columnSource : normalized.columns),
    };
    const existing = grouped.get(groupKey);

    if (existing) {
      existing.rows.push(row);
      existing.totalCost += row.totalCost;
      return;
    }

    grouped.set(groupKey, {
      id: normalized.pmcConfigId ?? normalized.id ?? `pmc-group-${index + 1}`,
      title: normalized.headName || normalized.category,
      category: normalized.category,
      description: normalized.configDescription || buildPmcHeadPresentation(normalized.headName, normalized.category).description,
      columns,
      rows: [row],
      totalCost: row.totalCost,
    });
  });

  return Array.from(grouped.values());
}

function mapApiLineItemToWorkingRow(item = {}, index = 0, quotationType = "Supply") {
  return {
    id: `api-row-${item.id ?? item.lineItemId ?? index + 1}`,
    description: item.description || item.productName || item.systemName || "",
    brandId: String(item.brandId ?? ""),
    make: item.brandName || item.brandId || "",
    productId: String(item.productId ?? item.productCode ?? ""),
    partNo: item.productCode || item.partNo || item.productId || "",
    uom: item.uom || "Nos",
    qty: String(item.quantity ?? 1),
    listPrice: item.listPrice || item.tPrice ? formatCurrency(item.listPrice ?? item.tPrice ?? 0) : "",
    netPrice: String(item.netPrice ?? item.netPriceAmount ?? item.netPriceRulePct ?? ""),
    discount: String(item.discountPct ?? ""),
    initialUnitRate: String(item.initialUnitRate ?? item.initRate ?? ""),
    multiplier: String(item.multiplier ?? "1"),
    unitRate: String(item.unitRate ?? item.netRate ?? item.netPrice ?? ""),
    installationPerUnit: String(
      item.inputInstallationRate ??
      item.installationPerUnit ??
      (quotationType === "SITC" ? "" : "0")
    ),
    installationMultiplier: String(item.installationMultiplier ?? "1"),
    remarks: item.remarks || "",
    systemName: item.systemName || "",
  };
}

function normalizeApiQuotation(item = {}) {
  const type = normalizeQuotationType(item.type);
  const fallbackDate = currentIsoDate();
  const workingRows = extractApiCollection(item.lineItems).map((lineItem, index) =>
    mapApiLineItemToWorkingRow(lineItem, index, type)
  );
  const pmcs = extractApiCollection(item.pmcs).map(normalizePmcEntry);
  const specificTerms = extractApiCollection(item.specificTerms).map((term, index) => ({
    id: term.id ?? `specific-term-${index + 1}`,
    title: term.title || `Term ${index + 1}`,
    description: term.description || "",
    displayOrder: Number(term.displayOrder ?? index + 1),
  }));

  const status = normalizeStatusLabel(item.status);
  const notes = resolveQuotationNotes(item, status);

  return hydrateQuotation({
    id: extractNumericId(item.id ?? item.quotationId),
    refId: item.refId || item.referenceId || item.refNo || item.quotationRefId || String(extractNumericId(item.id ?? item.quotationId) || ""),
    version: Number(item.versionNo ?? item.version ?? 1),
    customer: item.customerName || item.customer || item.customerDisplayName || "",
    customerId: extractNumericId(item.customerId),
    location: item.locationName || item.location || item.address || "",
    locationId: extractNumericId(item.locationId),
    pointOfContact: item.contactName || item.pointOfContactName || item.pointOfContact || "",
    phone: item.contactPhone || item.phone || "",
    email: item.contactEmail || item.email || "",
    salesExecutive: item.salesExecutive || item.salesExecutiveName || "",
    salesSupport: item.salesSupport || item.salesSupportName || "",
    type,
    complaintId: item.complaintId ?? item.complaint_id ?? "",
    status,
    sellingPrice: formatCurrency(
      item.finalSellingPrice ??
      item.sellingPrice ??
      item.customerSellingTotal ??
      item.totalSellingPrice ??
      0
    ),
    tenderDue: item.tenderDueDate || item.tenderDue || item.expiryDate || fallbackDate,
    expiry: item.expiryDate || item.expiry || fallbackDate,
    updated: String(item.updatedAt || item.lastUpdatedAt || item.updatedOn || item.createdAt || fallbackDate).slice(0, 10),
    workingRows: workingRows.length ? workingRows : [buildBlankWorkingRow(type)],
    pmcs,
    overheadsPct: String(item.overheadsPct ?? item.overheadsPercentage ?? 0),
    markUpPct: String(item.markUpPct ?? item.markupPct ?? item.markUpPercentage ?? 0),
    totalPmcCost: Number(item.totalPmcCost ?? 0) || pmcs.reduce((sum, pmc) => sum + Number(pmc.totalCost || 0), 0),
    specificTerms,
    additionalTerms: "",
    submissionNote: notes.submissionNote,
    revisionNote: notes.revisionNote,
    approverRoleName: item.approverRoleName || "",
    requiredApprovalLevel: item.requiredApprovalLevel || "",
  });
}

function buildWorkingSheetPayload(
  quotation,
  rows,
  additionalTermEntries,
  pmcEntries = [],
  pricing = {},
  termsContext = {}
) {
  const totalPmcCost = pmcEntries.reduce((sum, entry) => sum + Number(entry.totalCost || 0), 0);
  const specificTerms = Array.isArray(termsContext.specificTerms)
    ? termsContext.specificTerms
    : buildSpecificTermsPayload(
      termsContext.standardTerms || [],
      termsContext.selectedStandardTerms || [],
      additionalTermEntries,
      termsContext.previewOrder || []
    );

  return {
    overheadsPct: Number(pricing.overheadsPct ?? quotation.overheadsPct ?? 0) || 0,
    markUpPct: Number(pricing.markUpPct ?? quotation.markUpPct ?? 0) || 0,
    totalPmcCost,
    lineItems: rows.map((row, index) => ({
      slNo: index + 1,
      description: row.description || "",
      brandId: String(row.brandId || row.make || ""),
      productId: String(row.productId || row.partNo || ""),
      uom: row.uom || "",
      quantity: parseCurrency(row.qty) || 1,
      remarks: row.remarks || "",
      systemName: row.systemName || row.description || "",
      netPriceRulePct: parseCurrency(row.netPrice),
      discountPct: parseCurrency(row.discount),
      multiplier: parseCurrency(row.multiplier),
      inputInstallationRate: parseCurrency(row.installationPerUnit),
    })),
    pmcs: pmcEntries.map((entry, index) => ({
      id: Number(entry.id) || 0,
      pmcConfigId: Number(entry.pmcConfigId) || 0,
      category: entry.category || "",
      headName: entry.headName || entry.description || `PMC Head ${index + 1}`,
      description: entry.description || "",
      nos: parseCurrency(entry.nos),
      months: parseCurrency(entry.months),
      rate: parseCurrency(entry.rate ?? entry.unitRate ?? 0),
      boqQty: parseCurrency(entry.boqQty),
      consQty: parseCurrency(entry.consQty),
      unitRate: parseCurrency(entry.unitRate ?? entry.rate ?? 0),
      totalCost: computePmcRowTotal(
        entry,
        entry.rawColumns?.length ? entry.rawColumns : entry.columns || []
      ) || Number(entry.totalCost || 0) || 0,
    })),
    specificTerms,
  };
}

function buildBlankWorkingRow(type) {
  return {
    id: `row-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
    description: "",
    brandId: "",
    make: "",
    productId: "",
    partNo: "",
    uom: "",
    qty: "",
    listPrice: "",
    netPrice: "",
    discount: "",
    initialUnitRate: "",
    multiplier: "1.00",
    unitRate: "",
    installationPerUnit: type === "SITC" ? "" : "0",
    installationMultiplier: type === "SITC" ? "1.00" : "0",
    remarks: "",
  };
}

function validateWorkingRowsForSubmit(rows = []) {
  const invalidRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => {
      const hasCatalogReference = String(row.partNo || "").trim() || String(row.productId || "").trim();
      if (!hasCatalogReference) return false;
      return parseCurrency(row.listPrice) <= 0;
    });

  if (!invalidRows.length) return "";

  const rowNumbers = invalidRows.map(({ index }) => index + 1).join(", ");
  return `Row ${rowNumbers} ${invalidRows.length === 1 ? "has" : "have"} a part number but no catalogue list price. Select a valid catalogue part or enter pricing before submitting.`;
}

function buildRowFromCatalogProduct(row, product = {}) {
  const listPrice = Number(product.listPrice || 0);
  return {
    ...row,
    productId: product.id ? String(product.id) : row.productId,
    partNo: product.partNumber || row.partNo,
    make: product.make || product.manufacturer || row.make,
    brandId: product.make || product.manufacturer || row.brandId || row.make,
    description: row.description || product.name || product.description || "",
    uom: row.uom || product.uom || product.unitOfMeasure || "",
    listPrice: listPrice > 0 ? formatInputNumber(listPrice) : "",
    netPrice: "",
    initialUnitRate: "",
    unitRate: "",
  };
}

function hydrateQuotation(quotation) {
  const rows = Array.isArray(quotation.workingRows) && quotation.workingRows.length
    ? quotation.workingRows
    : [buildBlankWorkingRow(quotation.type)];

  return {
    ...quotation,
    versionLabel: `V${quotation.version}`,
    statusTone: statusTone(quotation.status),
    updatedLabel: formatDate(quotation.updated),
    tenderDueLabel: formatDate(quotation.tenderDue),
    expiryLabel: formatDate(quotation.expiry),
    expired: isExpired(quotation.expiry),
    workingRows: rows,
    pmcs: Array.isArray(quotation.pmcs) ? quotation.pmcs : [],
    specificTerms: Array.isArray(quotation.specificTerms) ? quotation.specificTerms : [],
    overheadsPct: String(quotation.overheadsPct ?? "0"),
    markUpPct: String(quotation.markUpPct ?? "0"),
    totalPmcCost: Number(quotation.totalPmcCost ?? 0) || 0,
  };
}

function createAdditionalTermEntry(index = 0, overrides = {}) {
  return {
    id: overrides.id || `custom-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
    title: overrides.title ?? "",
    description: overrides.description || "",
  };
}

function normalizeAdditionalTermEntry(entry = {}, index = 0) {
  if (typeof entry === "string") {
    return createAdditionalTermEntry(index, { description: entry });
  }
  return {
    id: entry.id || `custom-${index + 1}`,
    title: entry.title ?? "",
    description: entry.description || "",
  };
}

function getAdditionalTermDisplayTitle(entry, index) {
  return String(entry.title || "").trim() || `Additional term ${index + 1}`;
}

function getVisibleTerms(standardTerms, selectedIds, additionalTermEntries, order) {
  const baseTerms = standardTerms
    .filter((term) => selectedIds.includes(term.id))
    .map((term) => ({
      id: term.id,
      title: term.title,
      description: term.description,
      source: "standard",
      libraryId: term.id,
    }));

  const additional = (Array.isArray(additionalTermEntries) ? additionalTermEntries : [])
    .map((entry, index) => normalizeAdditionalTermEntry(entry, index))
    .filter((entry) => String(entry.description || "").trim() || String(entry.title || "").trim())
    .map((entry, index) => ({
      id: entry.id,
      title: getAdditionalTermDisplayTitle(entry, index),
      description: entry.description,
      source: "custom",
      libraryId: null,
    }));

  const combined = [...baseTerms, ...additional];
  const lookup = new Map(combined.map((item) => [item.id, item]));
  const ordered = order
    .map((id) => lookup.get(id))
    .filter(Boolean);
  const missing = combined.filter((item) => !order.includes(item.id));
  return [...ordered, ...missing];
}

function buildSpecificTermsPayload(standardTerms = [], selectedIds = [], additionalTermEntries = [], previewOrder = []) {
  return getVisibleTerms(standardTerms, selectedIds, additionalTermEntries, previewOrder).map((term, index) => ({
    id: term.source === "standard" ? Number(term.libraryId) || 0 : 0,
    title: term.title || `Term ${index + 1}`,
    description: term.description || "",
    displayOrder: index + 1,
  }));
}

function resolveTermsFromQuotation(specificTerms = [], libraryTerms = []) {
  const sorted = [...specificTerms].sort(
    (a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
  );
  const selectedIds = [];
  const additionalTermEntries = [];
  const previewOrder = [];

  sorted.forEach((term) => {
    const description = String(term.description || "").trim();
    const title = String(term.title || "").trim();
    const match = libraryTerms.find((libraryTerm) => {
      if (term.id != null && libraryTerm.id != null && String(libraryTerm.id) === String(term.id)) {
        return true;
      }
      return (
        String(libraryTerm.title || "").trim() === title &&
        String(libraryTerm.description || "").trim() === description
      );
    });

    if (match) {
      selectedIds.push(match.id);
      previewOrder.push(match.id);
      return;
    }

    if (!description && !title) return;
    const customId = `custom-${additionalTermEntries.length}`;
    additionalTermEntries.push({
      id: customId,
      title: title,
      description,
    });
    previewOrder.push(customId);
  });

  return {
    selectedIds,
    additionalTermEntries,
    previewOrder,
  };
}

function computePreparedWorkingRow(row = {}, quotationType = "Supply") {
  const qty = parseCurrency(row.qty);
  const listPrice = parseCurrency(row.listPrice);
  const netPrice = String(row.netPrice ?? "").trim()
    ? parseCurrency(row.netPrice)
    : listPrice > 0
      ? listPrice / 2
      : 0;
  const discount = parseCurrency(row.discount);
  const initialUnitRate = String(row.initialUnitRate ?? "").trim()
    ? parseCurrency(row.initialUnitRate)
    : netPrice * (1 - (discount / 100));
  const multiplier = parseCurrency(row.multiplier || 1);
  const unitRate = String(row.unitRate ?? "").trim()
    ? parseCurrency(row.unitRate)
    : initialUnitRate * multiplier;
  const installationPerUnit = parseCurrency(row.installationPerUnit);
  const installationMultiplier = quotationType === "SITC" ? parseCurrency(row.installationMultiplier || 1) : 0;
  const initialAmount = initialUnitRate * qty;
  const sellingSupplyAmount = unitRate * qty;
  const inputInstallationAmount = installationPerUnit * qty;
  const sellingInstallationRate = quotationType === "SITC"
    ? installationPerUnit * installationMultiplier
    : 0;
  const sellingInstallationAmount = quotationType === "SITC"
    ? sellingInstallationRate * qty
    : 0;

  return {
    ...row,
    netPrice: String(row.netPrice ?? "").trim() ? row.netPrice : formatInputNumber(netPrice),
    initialUnitRate: String(row.initialUnitRate ?? "").trim() ? row.initialUnitRate : formatInputNumber(initialUnitRate),
    unitRate: String(row.unitRate ?? "").trim() ? row.unitRate : formatInputNumber(unitRate),
    initialAmount: formatCurrency(initialAmount),
    amount: formatCurrency(sellingSupplyAmount),
    installationAmount: formatCurrency(sellingInstallationAmount),
    inputInstallationAmount: formatCurrency(inputInstallationAmount),
    sellingInstallationRate: formatCurrency(sellingInstallationRate),
    numericTotals: {
      initialAmount,
      sellingSupplyAmount,
      inputInstallationAmount,
      sellingInstallationRate,
      sellingInstallationAmount,
    },
  };
}

function applyNetPriceRuleToRows(rows = [], netPricePercentage = 0) {
  const percentage = Number(netPricePercentage) || 0;
  if (!percentage) return rows;

  return rows.map((row) => {
    if (String(row.netPrice || "").trim()) {
      return row;
    }

    const listPrice = parseCurrency(row.listPrice);
    if (!listPrice) {
      return row;
    }

    const derivedNetPrice = listPrice - ((listPrice * percentage) / 100);
    return {
      ...row,
      netPrice: String(Number(derivedNetPrice.toFixed(2))),
    };
  });
}

function computeWorkingTotals(rows, quotationType) {
  const preparedRows = rows.map((row) => computePreparedWorkingRow(row, quotationType));
  const totals = preparedRows.reduce((sum, row) => ({
    supplyInputAmount: sum.supplyInputAmount + row.numericTotals.initialAmount,
    supplySellingAmount: sum.supplySellingAmount + row.numericTotals.sellingSupplyAmount,
    inputInstallationAmount: sum.inputInstallationAmount + row.numericTotals.inputInstallationAmount,
    sellingInstallationAmount: sum.sellingInstallationAmount + row.numericTotals.sellingInstallationAmount,
  }), {
    supplyInputAmount: 0,
    supplySellingAmount: 0,
    inputInstallationAmount: 0,
    sellingInstallationAmount: 0,
  });

  const supplySubtotal = totals.supplySellingAmount;
  const installationSubtotal = quotationType === "SITC" ? totals.sellingInstallationAmount : 0;
  return {
    supplyInputAmount: totals.supplyInputAmount,
    supplySubtotal,
    inputInstallationAmount: totals.inputInstallationAmount,
    installationSubtotal,
    customerSellingTotal: supplySubtotal + installationSubtotal,
  };
}

function STATUS_STYLES({ tone }) {
  const map = {
    draft: "bg-[#eef2f8] text-[#586c8f]",
    pending: "bg-[#ffefbf] text-[#d78600]",
    approved: "bg-[#dde8ff] text-[#2743a0]",
    sent: "bg-[#e8f4ff] text-[#1d7dc2]",
    negotiation: "bg-[#f5ebff] text-[#7d4cbc]",
    accepted: "bg-[#dff8e8] text-[#1f9b56]",
    rejected: "bg-[#fde8e5] text-[#c74b40]",
    superseded: "bg-[#eef2f8] text-[#7b8faa]",
  };
  return map[tone] || map.draft;
}

function StatusPill({ tone, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[14px] px-3 py-1.5 text-[12px] font-semibold ${STATUS_STYLES({ tone })}`}>
      <span className="h-2 w-2 rounded-full bg-current opacity-95" />
      {translateQuotationStatus(label)}
    </span>
  );
}

function SectionShell({ title, children, action, compact = false }) {
  return (
    <section className={`overflow-hidden border border-[#d8e0ed] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] ${compact ? "rounded-[12px]" : "rounded-[14px]"}`}>
      <div className={`flex items-center justify-between border-b border-[#d9e1ee] ${compact ? "px-4 py-2.5 sm:px-5" : "px-5 py-4 sm:px-6"}`}>
        <h3 className={`font-semibold tracking-[-0.02em] text-[#2d394c] ${compact ? "text-[13px]" : "text-[15px]"}`}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function QuotationPrimaryButton({ children, onClick, icon: Icon = Plus, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[46px] items-center justify-center gap-2.5 rounded-[12px] bg-[#243c93] px-5 text-[13px] font-semibold text-white shadow-[0_14px_28px_-20px_rgba(36,60,147,0.8)] transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6] sm:px-6 sm:text-[14px]"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
    </button>
  );
}

function QuotationSecondaryButton({ children, onClick, icon: Icon = FileText, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-[12px] border border-[#d0d9ea] bg-white px-4 text-[13px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:text-[#8da0be]"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
    </button>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-3 px-5 py-5">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="grid grid-cols-9 gap-3">
          {Array.from({ length: 9 }).map((_, column) => (
            <div key={column} className="h-10 animate-pulse rounded-[10px] bg-[#edf1f7]" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#eef2ff] text-[#2944a8]">
        <FileText className="h-6 w-6" />
      </div>
      <h3 className="text-[18px] font-semibold text-[#23324c]">{sq("emptyTitle")}</h3>
      <p className="mt-2 max-w-xl text-[13px] leading-6 text-[#7184a3]">
        {sq("emptyHint")}
      </p>
      <div className="mt-5">
        <QuotationPrimaryButton onClick={onCreate}>{sq("createQuotation")}</QuotationPrimaryButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="mx-auto mb-4 flex items-start justify-between gap-4 rounded-[14px] border border-[#f4cfcb] bg-[#fff6f5] px-5 py-4 text-[#b04d43]">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="text-[13px] font-semibold">{sq("errorTitle")}</p>
          <p className="mt-1 text-[12px] leading-5">{message}</p>
        </div>
      </div>
      <button type="button" onClick={onRetry} className="text-[12px] font-semibold text-[#2944a8]">
        {sq("retry")}
      </button>
    </div>
  );
}

function buildQuotationFromForm(form, options = {}) {
  const fallbackDate = currentIsoDate();
  return hydrateQuotation({
    id: options.id ?? null,
    refId: form.refId.trim().toUpperCase(),
    version: 1,
    customer: form.customer,
    customerId: options.customerId ?? null,
    location: form.location,
    locationId: options.locationId ?? null,
    pointOfContact: form.contactName,
    phone: form.phone,
    email: form.email,
    salesExecutive: form.salesExecutive,
    salesSupport: form.salesSupport,
    type: form.type,
    complaintId: form.type === "SERVICE" ? form.complaintId || "" : "",
    status: "Draft",
    sellingPrice: "Rs0",
    tenderDue: form.expiry || fallbackDate,
    expiry: form.expiry,
    updated: fallbackDate,
    workingRows: [buildBlankWorkingRow(form.type)],
    additionalTerms: "",
  });
}

function CreateQuotationForm() {
  useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    refId: "",
    type: "",
    complaintId: "",
    salesExecutive: "",
    salesSupport: "",
    customer: "",
    location: "",
    contactName: "",
    phone: "",
    email: "",
    tenderDueDate: "",
    expiry: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [complaintOptions, setComplaintOptions] = useState([]);
  const [, setComplaintPage] = useState(0);
  const [complaintHasMore, setComplaintHasMore] = useState(false);
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [loadingMoreComplaints, setLoadingMoreComplaints] = useState(false);

  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [customerApiError, setCustomerApiError] = useState("");
  const [employeeApiError, setEmployeeApiError] = useState("");
  const [complaintApiError, setComplaintApiError] = useState("");
  const loadingMoreComplaintsRef = useRef(false);
  const complaintPageRef = useRef(0);
  const complaintHasMoreRef = useRef(false);

  const selectedCustomer = customerOptions.find((item) => item.name === form.customer) || null;
  const selectedLocation = locationOptions.find((item) => item.name === form.location) || null;
  const isServiceType = form.type === "SERVICE";

  function patchForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setSubmitError("");
  }

  function handleQuotationTypeChange(nextType) {
    setForm((current) => ({
      ...current,
      type: nextType,
      complaintId: nextType === "SERVICE" ? current.complaintId : "",
    }));
    setErrors((current) => ({ ...current, type: "", complaintId: "" }));
    setSubmitError("");
    if (nextType !== "SERVICE") {
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      setLoadingMoreComplaints(false);
      loadingMoreComplaintsRef.current = false;
      setComplaintApiError("");
    }
  }

  async function fetchComplaintPage(page, { append = false } = {}) {
    const response = await searchComplaintsByStatus("In_Progress", {
      page,
      size: 10,
      sortBy: "complaintId",
      sortDir: "DESC",
    });
    const mapped = (Array.isArray(response?.items) ? response.items : [])
      .map(mapComplaintOption)
      .filter(Boolean);

    setComplaintOptions((current) => (append ? mergeUniqueComplaintOptions(current, mapped) : mergeUniqueComplaintOptions([], mapped)));
    setComplaintPage(page);
    complaintPageRef.current = page;
    const hasMore =
      mapped.length === 0
        ? false
        : typeof response?.hasMore === "boolean"
          ? response.hasMore
          : mapped.length >= 10;
    setComplaintHasMore(hasMore);
    complaintHasMoreRef.current = hasMore;
    return response;
  }

  async function loadMoreComplaints() {
    if (
      !isServiceType ||
      !complaintHasMoreRef.current ||
      loadingComplaints ||
      loadingMoreComplaintsRef.current
    ) {
      return;
    }

    loadingMoreComplaintsRef.current = true;
    setLoadingMoreComplaints(true);
    setComplaintApiError("");

    try {
      await fetchComplaintPage(complaintPageRef.current + 1, { append: true });
    } catch (error) {
      setComplaintApiError(error.message || "Unable to load more complaint IDs.");
    } finally {
      loadingMoreComplaintsRef.current = false;
      setLoadingMoreComplaints(false);
    }
  }

  // 1. Fetch Customers: GET /saas/crm/customer-info/search?page=0&size=10&sortBy=customerId&sortDir=ASC
  useEffect(() => {
    let active = true;

    (async () => {
      setLoadingCustomers(true);
      try {
        const response = await customerService.getCustomers(0, 10, "customerId", "ASC");
        if (!active) return;

        const customers = Array.isArray(response?.customers)
          ? response.customers.map(normalizeCustomerOption).filter((item) => item.id && item.name)
          : [];

        setCustomerOptions(customers);
        setCustomerApiError("");
      } catch (error) {
        if (!active) return;
        setCustomerOptions([]);
        setCustomerApiError(error.message || "Unable to load CRM customer details.");
      } finally {
        if (active) setLoadingCustomers(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Fetch Employees for Sales Executive / Sales Support
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const response = await hrmsGet("/emp-info/search?page=0&size=200&sortBy=empId&sortDir=ASC", "HR01S");
        if (!active) return;
        const payload = response?.data ?? response;
        const employees = extractApiCollection(payload)
          .map(normalizeEmployeeOption)
          .filter((item) => item.id && item.name);

        setEmployeeOptions(employees);
        setEmployeeApiError("");
      } catch (error) {
        if (!active) return;
        setEmployeeOptions([]);
        setEmployeeApiError(error.message || "Unable to load employee options.");
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Fetch Complaint IDs when Quotation Type is SERVICE (page 0 first, then infinite scroll)
  useEffect(() => {
    let active = true;

    if (!isServiceType) {
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      setLoadingComplaints(false);
      setLoadingMoreComplaints(false);
      loadingMoreComplaintsRef.current = false;
      setComplaintApiError("");
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoadingComplaints(true);
      setComplaintApiError("");
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      try {
        await fetchComplaintPage(0);
      } catch (error) {
        if (!active) return;
        setComplaintOptions([]);
        setComplaintHasMore(false);
        complaintHasMoreRef.current = false;
        setComplaintApiError(error.message || "Unable to load complaint IDs.");
      } finally {
        if (active) setLoadingComplaints(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isServiceType]);

  // 2 & 3. Fetch Locations and Contacts after customer selection
  // GET /saas/crm/customer-locations/search/customer?customerId={customerId}&page=0&size=10&sortBy=locationId&sortDir=ASC
  // GET /saas/crm/customer-contact/search/customer?customerId={customerId}&page=0&size=10&sortBy=contactId&sortDir=ASC
  useEffect(() => {
    let active = true;

    if (!selectedCustomer?.id) {
      setLocationOptions([]);
      setContactOptions([]);
      return;
    }

    const customerId = selectedCustomer.id;

    setLoadingLocations(true);
    customerService
      .getLocationsByCustomer(customerId, 0, 10, "locationId", "ASC")
      .then((response) => {
        if (!active) return;
        const locations = Array.isArray(response?.locations)
          ? response.locations.map(normalizeLocationOption).filter((item) => item.id && item.name)
          : [];
        setLocationOptions(locations);
      })
      .catch((error) => {
        if (!active) return;
        setLocationOptions([]);
        console.warn("Failed to load customer locations:", error);
      })
      .finally(() => {
        if (active) setLoadingLocations(false);
      });

    setLoadingContacts(true);
    customerService
      .getContactsByCustomer(customerId, 0, 10, "contactId", "ASC")
      .then((response) => {
        if (!active) return;
        const contacts = Array.isArray(response?.contacts)
          ? response.contacts.map(normalizeContactOption).filter((item) => item.id && item.name)
          : [];
        setContactOptions(contacts);
      })
      .catch((error) => {
        if (!active) return;
        setContactOptions([]);
        console.warn("Failed to load customer contacts:", error);
      })
      .finally(() => {
        if (active) setLoadingContacts(false);
      });

    return () => {
      active = false;
    };
  }, [selectedCustomer?.id]);

  function handleCustomerChange(event) {
    const customerName = event.target.value;
    if (customerName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    patchForm("customer", customerName);
    setForm((current) => ({
      ...current,
      customer: customerName,
      location: "",
      contactName: "",
      phone: "",
      email: "",
    }));
    setErrors((current) => ({
      ...current,
      customer: "",
      location: "",
      contactName: "",
    }));
  }

  function handleLocationChange(event) {
    const locationName = event.target.value;
    if (locationName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    patchForm("location", locationName);
  }

  function handleContactChange(event) {
    const contactName = event.target.value;
    if (contactName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    patchForm("contactName", contactName);
    const foundContact = contactOptions.find((item) => item.name === contactName);
    if (foundContact) {
      setForm((current) => ({
        ...current,
        contactName,
        phone: foundContact.phone || "",
        email: foundContact.email || "",
      }));
    } else {
      setForm((current) => ({
        ...current,
        contactName,
        phone: "",
        email: "",
      }));
    }
  }

  function validate() {
    const next = {};
    if (!form.refId.trim()) next.refId = "Ref ID is required";
    if (!form.type) next.type = "Quotation type is required";
    if (form.type === "SERVICE" && !String(form.complaintId || "").trim()) {
      next.complaintId = sq("complaintIdRequired");
    }
    if (!form.customer) next.customer = "Customer is required";
    if (!form.location) next.location = "Address or location is required";
    if (!form.expiry) next.expiry = "Quotation expiry date is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleCreate() {
    if (!validate()) return;
    if (!selectedCustomer || !selectedLocation) {
      setSubmitError("Please choose a valid customer and saved location.");
      return;
    }

    const draftQuotation = buildQuotationFromForm(form, {
      customerId: selectedCustomer.id,
      locationId: selectedLocation.id,
    });

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {
        refId: form.refId.trim().toUpperCase(),
        type: mapQuotationTypeForApi(form.type),
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        address: selectedLocation.name,
        contactName: form.contactName.trim(),
        pointOfContactName: form.contactName.trim(),
        contactPhone: form.phone.trim(),
        contactEmail: form.email.trim(),
        tenderDueDate: form.tenderDueDate || form.expiry,
        expiryDate: form.expiry,
        salesExecutive: form.salesExecutive || undefined,
        salesSupport: form.salesSupport || undefined,
        ...(form.type === "SERVICE"
          ? { complaintId: Number(form.complaintId) || form.complaintId }
          : {}),
      };

      const response = await createQuotation(payload);

      const createdQuotation = response?.data && typeof response.data === "object" && !Array.isArray(response.data)
        ? normalizeApiQuotation({
            ...response.data,
            customerName: draftQuotation.customer,
            locationName: draftQuotation.location,
            salesExecutive: draftQuotation.salesExecutive,
            salesSupport: draftQuotation.salesSupport,
          })
        : draftQuotation;
      const quotationId = createdQuotation.id ?? response?.data?.id ?? response?.id ?? draftQuotation.refId;

      navigate(`/sales/quotations/quotation/${quotationId}`, {
        state: {
          quotation: createdQuotation,
          versionHistory: [createdQuotation],
          createdFromFlow: true,
        },
      });
    } catch (error) {
      const message = String(error.message || "");
      if (
        error.status === 409 ||
        (error.status === 400 && /ref.*(already|exist|duplicate|use)/i.test(message)) ||
        /ref id already in use/i.test(message) ||
        /already in use/i.test(message) ||
        /already exists/i.test(message)
      ) {
        setErrors((current) => ({ ...current, refId: "Ref id already in use" }));
        setSubmitError("Ref id already in use");
      } else {
        setSubmitError(message || "Failed to create quotation.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isCreateDisabled =
    !form.refId.trim() ||
    !form.type ||
    (form.type === "SERVICE" && !String(form.complaintId || "").trim()) ||
    !form.customer ||
    !form.location ||
    !form.expiry ||
    submitting;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate("/sales/quotations/quotation")}
        className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#2944a8] transition hover:text-[#1d327f]"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 px-0.5">
        <div className="min-w-0">
          <h2 className={`${ui.pageTitle} !text-[20px]`}>{sq("createFormTitle")}</h2>
        </div>
        <StatusPill tone="draft" label={sq("statusDraft")} />
      </div>

      {(submitError || customerApiError || employeeApiError || complaintApiError) && (
        <ErrorState
          message={submitError || customerApiError || employeeApiError || complaintApiError}
          onRetry={() => {
            setSubmitError("");
            setCustomerApiError("");
            setEmployeeApiError("");
            setComplaintApiError("");
          }}
        />
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionShell title={sq("quotationDetails")}>
          <div className="grid gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
            <FormField label={sq("refId")} required error={errors.refId}>
              <input
                value={form.refId}
                onChange={(event) => patchForm("refId", event.target.value)}
                className={inputClass(Boolean(errors.refId))}
                placeholder={sq("refId")}
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("quotationType")} required error={errors.type}>
              <select
                value={form.type}
                onChange={(event) => handleQuotationTypeChange(event.target.value)}
                className={inputClass(Boolean(errors.type))}
                {...localizedFieldProps()}
              >
                <option value="">{sq("selectQuotationType")}</option>
                {SALES_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {translateQuotationType(option)}
                  </option>
                ))}
              </select>
            </FormField>

            {isServiceType && (
              <FormField label={sq("complaintId")} required error={errors.complaintId}>
                <ComplaintIdInfiniteSelect
                  value={form.complaintId}
                  onChange={(nextValue) => patchForm("complaintId", nextValue)}
                  options={complaintOptions}
                  loading={loadingComplaints}
                  loadingMore={loadingMoreComplaints}
                  hasMore={complaintHasMore}
                  onLoadMore={loadMoreComplaints}
                  disabled={loadingComplaints && complaintOptions.length === 0}
                  hasError={Boolean(errors.complaintId)}
                  placeholder={sq("selectComplaintId")}
                />
              </FormField>
            )}

            <FormField label={sq("tenderDueDate")}>
              <input
                type="date"
                value={form.tenderDueDate}
                onChange={(event) => patchForm("tenderDueDate", event.target.value)}
                className={inputClass(false)}
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("quotationExpiryDate")} required error={errors.expiry}>
              <input
                type="date"
                value={form.expiry}
                onChange={(event) => patchForm("expiry", event.target.value)}
                className={inputClass(Boolean(errors.expiry))}
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("salesExecutive")}>
              <select
                value={form.salesExecutive}
                onChange={(event) => patchForm("salesExecutive", event.target.value)}
                className={inputClass(false)}
                disabled={employeeOptions.length === 0}
                {...localizedFieldProps()}
              >
                <option value="">{sq("selectSalesExecutive")}</option>
                {employeeOptions.map((option) => (
                  <option key={option.id} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={sq("salesSupport")}>
              <select
                value={form.salesSupport}
                onChange={(event) => patchForm("salesSupport", event.target.value)}
                className={inputClass(false)}
                disabled={employeeOptions.length === 0}
                {...localizedFieldProps()}
              >
                <option value="">{sq("selectSalesSupport")}</option>
                {employeeOptions.map((option) => (
                  <option key={`${option.id}-support`} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </SectionShell>

        <SectionShell title={sq("customerDetails")}>
          <div className="grid gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
            <FormField label={sq("customerName")} required error={errors.customer}>
              <select
                value={form.customer}
                onChange={handleCustomerChange}
                disabled={loadingCustomers}
                className={inputClass(Boolean(errors.customer))}
                {...localizedFieldProps()}
              >
                <option value="">{loadingCustomers ? "Loading..." : sq("selectCustomer")}</option>
                {customerOptions.map((customer) => (
                  <option key={customer.id} value={customer.name}>
                    {customer.name}
                  </option>
                ))}
                <option value={ADD_ENTITY_OPTION}>+ {sq("addNewCustomer")}</option>
              </select>
            </FormField>

            <FormField
              label={sq("addressOrLocation")}
              required
              error={errors.location}
            >
              <select
                value={form.location}
                onChange={handleLocationChange}
                disabled={loadingLocations}
                className={inputClass(Boolean(errors.location))}
                {...localizedFieldProps()}
              >
                <option value="">
                  {!form.customer
                    ? sq("selectCustomer")
                    : loadingLocations
                    ? "Loading..."
                    : locationOptions.length
                    ? sq("selectLocation")
                    : sq("noSavedLocations")}
                </option>
                {locationOptions.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                  </option>
                ))}
                <option value={ADD_ENTITY_OPTION}>+ {sq("addNewLocation")}</option>
              </select>
            </FormField>

            <FormField label={sq("pointOfContactName")}>
              <select
                value={form.contactName}
                onChange={handleContactChange}
                disabled={loadingContacts}
                className={inputClass(false)}
                {...localizedFieldProps()}
              >
                <option value="">
                  {!form.customer
                    ? sq("selectCustomer")
                    : loadingContacts
                    ? "Loading..."
                    : contactOptions.length
                    ? sq("selectContact")
                    : sq("noSavedContacts")}
                </option>
                {contactOptions.map((contact) => (
                  <option key={contact.id} value={contact.name}>
                    {contact.name}
                  </option>
                ))}
                <option value={ADD_ENTITY_OPTION}>+ {sq("addNewContact")}</option>
              </select>
            </FormField>

            <FormField label={sq("phoneNumber")}>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => patchForm("phone", event.target.value)}
                disabled={!form.customer}
                className={inputClass(false)}
                placeholder="+91 98..."
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("email")} fullWidth>
              <input
                type="email"
                value={form.email}
                onChange={(event) => patchForm("email", event.target.value)}
                disabled={!form.customer}
                className={inputClass(false)}
                placeholder="contact@company.com"
                {...localizedFieldProps()}
              />
            </FormField>
          </div>
        </SectionShell>
      </div>

      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <QuotationSecondaryButton icon={null} onClick={() => navigate("/sales/quotations/quotation")}>
          {i18n.t("formActions.cancel")}
        </QuotationSecondaryButton>
        <QuotationPrimaryButton icon={null} onClick={handleCreate} disabled={isCreateDisabled}>
          {submitting ? sq("creating") : sq("create")}
        </QuotationPrimaryButton>
      </div>
    </div>
  );
}

function FormField({ label, required = false, error, hint, children, fullWidth = false }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <label className="text-[12px] font-semibold text-[#51657f]">{label}</label>
        {required && <span className="text-[12px] text-[#c74b40]">*</span>}
      </div>
      {children}
      {error ? <p className="mt-1.5 text-[11px] font-medium text-[#c74b40]">{error}</p> : hint ? <p className="mt-1.5 text-[11px] text-[#8ea0bd]">{hint}</p> : null}
    </div>
  );
}

function mapComplaintOption(item = {}) {
  const complaintId = item.complaintId ?? item.id ?? item.raw?.complaintId;
  if (complaintId == null || complaintId === "") return null;
  return {
    id: String(complaintId),
    label: String(complaintId),
  };
}

function mergeUniqueComplaintOptions(current = [], incoming = []) {
  const next = [...current];
  const seen = new Set(current.map((item) => item.id));
  incoming.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    next.push(item);
  });
  return next;
}

function ComplaintIdInfiniteSelect({
  value,
  onChange,
  options,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  disabled = false,
  hasError = false,
  placeholder = "Select complaint ID",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  function handleScroll(event) {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 24) {
      onLoadMore?.();
    }
  }

  const selectedLabel = options.find((item) => item.id === value)?.label || value || "";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`${inputClass(hasError)} flex items-center justify-between text-left disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]`}
      >
        <span className={selectedLabel ? "text-[#2d394c]" : "text-[#96a5bb]"}>
          {loading && !options.length
            ? sq("loadingComplaints")
            : selectedLabel || (options.length ? placeholder : sq("noComplaintsAvailable"))}
        </span>
        <MoveDown className={`h-4 w-4 shrink-0 text-[#8ea0bd] transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 overflow-hidden rounded-[12px] border border-[#d1d9e8] bg-white shadow-[0_18px_36px_-24px_rgba(30,51,131,0.35)]">
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-56 overflow-y-auto py-1"
          >
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="block w-full px-4 py-2.5 text-left text-[13px] text-[#96a5bb] hover:bg-[#f5f8fd]"
            >
              {placeholder}
            </button>
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                className={`block w-full px-4 py-2.5 text-left text-[13px] hover:bg-[#f5f8fd] ${
                  value === option.id ? "bg-[#eef3ff] font-semibold text-[#2944a8]" : "text-[#2d394c]"
                }`}
              >
                {option.label}
              </button>
            ))}
            {(loadingMore || (hasMore && options.length > 0)) && (
              <div className="px-4 py-2 text-[11px] font-medium text-[#8ea0bd]">
                {loadingMore ? sq("loadingComplaints") : "Scroll for more"}
              </div>
            )}
            {!hasMore && options.length > 0 && !loadingMore && (
              <div className="px-4 py-2 text-[11px] text-[#a0afc4]">All complaint IDs loaded</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function inputClass(hasError) {
  return `min-h-[44px] w-full rounded-[12px] border bg-white px-4 text-[13px] text-[#2d394c] outline-none transition placeholder:text-[#96a5bb] ${
    hasError ? "border-[#e4a5a0]" : "border-[#d1d9e8] focus:border-[#95addd]"
  }`;
}

function filterControlClass() {
  return "h-9 w-full rounded-[10px] border border-[#d1d9e8] bg-white px-3 text-[12px] text-[#2d394c] outline-none transition placeholder:text-[#96a5bb] focus:border-[#95addd]";
}

const ADD_ENTITY_OPTION = "__add_new_entity__";

function LifecycleStepper({ status }) {
  if (!CUSTOMER_STAGE_STATUSES.includes(status)) return null;

  return (
    <div className="rounded-[12px] border border-[#d8e0ed] bg-white px-3 py-3 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] sm:px-4">
      <div className="flex flex-wrap gap-2">
        {STEP_STATUSES.map((step, index) => {
          const currentIndex = STEP_STATUSES.indexOf(status === "Accepted" || status === "Rejected" ? "Under Negotiation" : status);
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div key={step} className="flex min-w-[120px] flex-1 items-center gap-2 rounded-[10px] border border-[#e1e7f2] px-3 py-2">
              <div
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
                  isDone ? "bg-[#2944a8] text-white" : isCurrent ? "bg-[#eef2ff] text-[#2944a8]" : "bg-[#eef2f8] text-[#8ea0bd]"
                }`}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </div>
              <div>
                <p className={`text-[11px] font-semibold ${isCurrent ? "text-[#2944a8]" : "text-[#51657f]"}`}>{translateQuotationStatus(step)}</p>
                <p className="text-[10px] text-[#8ea0bd]">
                  {isDone ? sq("stepCompleted") : isCurrent ? sq("stepCurrent") : sq("stepUpcoming")}
                </p>
              </div>
            </div>
          );
        })}
        <div className={`flex min-w-[120px] flex-1 items-center gap-2 rounded-[10px] border px-3 py-2 ${status === "Accepted" ? "border-[#cdebd9] bg-[#f3fff7]" : status === "Rejected" ? "border-[#f2d0cc] bg-[#fff7f6]" : "border-[#e1e7f2]"}`}>
          <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${status === "Accepted" ? "bg-[#dff8e8] text-[#1f9b56]" : status === "Rejected" ? "bg-[#fde8e5] text-[#c74b40]" : "bg-[#eef2f8] text-[#8ea0bd]"}`}>
            {status === "Accepted" ? <Check className="h-3.5 w-3.5" /> : status === "Rejected" ? <XCircle className="h-3.5 w-3.5" /> : 6}
          </div>
          <div>
            <p className={`text-[11px] font-semibold ${status === "Accepted" ? "text-[#1f9b56]" : status === "Rejected" ? "text-[#c74b40]" : "text-[#51657f]"}`}>
              {sq("acceptedRejected")}
            </p>
            <p className="text-[10px] text-[#8ea0bd]">
              {status === "Accepted" ? sq("terminalAccepted") : status === "Rejected" ? sq("terminalRejected") : sq("terminalOutcome")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkingSheetMakeField({ value, makes, makesLoading, editable, onChange }) {
  if (!editable) {
    return (
      <div className="flex min-h-[44px] items-center px-2.5 py-2 text-[11px] text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  return (
    <div className="flex min-h-[44px] items-center bg-white px-2 py-2">
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={makesLoading}
        className="w-full border-0 bg-transparent text-[11px] text-[#34445f] outline-none disabled:text-[#8ea0bd]"
        {...localizedFieldProps()}
      >
        <option value="">{makesLoading ? "Loading..." : "Select make"}</option>
        {value && !makes.some((make) => make.name === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {makes.map((make) => (
          <option key={make.id || make.name} value={make.name}>
            {make.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function WorkingSheetPartNoField({ value, make, editable, onChange, onProductSelect }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const selectedMake = String(make || "").trim();
    if (!editable || !selectedMake) {
      setOptions([]);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchPartNumbersByMake(selectedMake)
      .then((products) => {
        if (cancelled) return;
        setOptions(products);
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setError(err.message || "Unable to load part numbers.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [make, editable]);

  async function handleSelect(partNumber) {
    onChange(partNumber);
    if (!partNumber) {
      onProductSelect(null);
      return;
    }

    const matched = options.find((item) => item.partNumber === partNumber);
    if (!matched) {
      onProductSelect({ partNumber, make });
      return;
    }

    let resolved = matched;
    if (matched.id) {
      try {
        const detailed = await fetchCatalogProductById(matched.id);
        if (detailed) resolved = detailed;
      } catch {
        resolved = matched;
      }
    }
    onProductSelect(resolved);
  }

  if (!editable) {
    return (
      <div className="flex min-h-[44px] items-center px-2.5 py-2 text-[11px] text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  const disabled = !String(make || "").trim() || loading;

  return (
    <div className="flex min-h-[44px] flex-col justify-center bg-white px-2 py-1">
      <select
        value={value || ""}
        onChange={(event) => handleSelect(event.target.value)}
        disabled={disabled}
        className="w-full border-0 bg-transparent text-[11px] text-[#34445f] outline-none disabled:text-[#8ea0bd]"
        {...localizedFieldProps()}
      >
        <option value="">
          {!String(make || "").trim()
            ? "Select make first"
            : loading
              ? "Loading..."
              : options.length
                ? "Select part no."
                : "No parts found"}
        </option>
        {value && !options.some((item) => item.partNumber === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {options.map((product) => (
          <option key={product.id || product.partNumber} value={product.partNumber}>
            {product.partNumber}
          </option>
        ))}
      </select>
      {error ? <span className="text-[9px] text-[#b35b62]">{error}</span> : null}
    </div>
  );
}

function WorkingSheetTab({ quotationType, rows, setRows, editable, onSave, saving }) {
  useTranslation();
  const showInstallation = quotationType === "SITC";
  const [makes, setMakes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [makesError, setMakesError] = useState("");

  useEffect(() => {
    let active = true;
    setMakesLoading(true);
    setMakesError("");

    fetchMakesFromProductCatalog()
      .then(({ makes: nextMakes }) => {
        if (!active) return;
        setMakes(nextMakes);
        if (!nextMakes.length) {
          setMakesError("No Make values returned by the product catalogue.");
        }
      })
      .catch((error) => {
        if (!active) return;
        setMakes([]);
        setMakesError(error.message || "Unable to load Make options from product catalogue.");
      })
      .finally(() => {
        if (active) setMakesLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);
  const workingColumns = [
    { key: "index", label: "#", readonly: true, cellClassName: "w-[56px]" },
    { key: "description", label: "Description", editable: true, cellClassName: "min-w-[150px]" },
    { key: "make", label: "Make", editable: true, cellClassName: "min-w-[110px]" },
    { key: "partNo", label: "Part No", editable: true, cellClassName: "min-w-[112px]" },
    { key: "uom", label: "UoM", editable: true, cellClassName: "min-w-[72px]" },
    { key: "qty", label: "Qty", editable: true, numeric: true, cellClassName: "min-w-[66px]" },
    { key: "listPrice", label: "List Price", readonly: true, warning: true, cellClassName: "min-w-[108px]" },
    { key: "netPrice", label: "Net Price", editable: true, numeric: true, cellClassName: "min-w-[108px]" },
    { key: "discount", label: "Disc %", editable: true, numeric: true, cellClassName: "min-w-[82px]" },
    { key: "initialUnitRate", label: "Input Supply Rate", editable: true, numeric: true, cellClassName: "min-w-[122px]" },
    { key: "initialAmount", label: "Input Supply Amt", readonly: true, shaded: true, cellClassName: "min-w-[122px]" },
    { key: "multiplier", label: "Mult.", editable: true, numeric: true, cellClassName: "min-w-[74px]" },
    { key: "unitRate", label: "Selling Supply Rate", editable: true, numeric: true, cellClassName: "min-w-[126px]" },
    { key: "amount", label: "Selling Supply Amt", readonly: true, accent: true, cellClassName: "min-w-[128px]" },
    ...(showInstallation ? [{ key: "installationPerUnit", label: "Install/U", editable: true, numeric: true, cellClassName: "min-w-[118px]" }] : []),
    ...(showInstallation ? [{ key: "inputInstallationAmount", label: "Install Amt", readonly: true, shaded: true, cellClassName: "min-w-[126px]" }] : []),
    ...(showInstallation ? [{ key: "installationMultiplier", label: "Install Mult.", editable: true, numeric: true, cellClassName: "min-w-[88px]" }] : []),
    ...(showInstallation ? [{ key: "sellingInstallationRate", label: "Selling Install Rate", readonly: true, accent: true, cellClassName: "min-w-[126px]" }] : []),
    ...(showInstallation ? [{ key: "installationAmount", label: "Selling Install Amt", readonly: true, accent: true, cellClassName: "min-w-[130px]" }] : []),
    { key: "remarks", label: "Remarks", editable: true, cellClassName: "min-w-[118px]" },
  ];

  function getCellTone(column) {
    if (column.accent) return "bg-[#eef2ff] font-semibold text-[#2944a8]";
    if (column.shaded) return "bg-[#f4f7fc] text-[#6a7f9f]";
    if (column.warning) return "bg-white text-[#34445f]";
    return "bg-white text-[#34445f]";
  }

  function updateRow(rowId, key, value) {
    setRows((current) =>
      current.map((row) => (row.id === rowId ? { ...row, [key]: value } : row))
    );
  }

  function updateBrand(rowId, makeName) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          brandId: makeName || "",
          make: makeName || "",
          productId: "",
          partNo: "",
          listPrice: "",
          netPrice: "",
          initialUnitRate: "",
          unitRate: "",
        };
      })
    );
  }

  function applyProductToRow(rowId, product) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        if (!product) {
          return {
            ...row,
            productId: "",
            partNo: "",
            listPrice: "",
            netPrice: "",
            initialUnitRate: "",
            unitRate: "",
          };
        }
        return buildRowFromCatalogProduct(row, product);
      })
    );
  }

  function updatePartNo(rowId, partNo) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          partNo,
          productId: partNo ? row.productId : "",
          listPrice: partNo ? row.listPrice : "",
          netPrice: partNo ? row.netPrice : "",
          initialUnitRate: partNo ? row.initialUnitRate : "",
          unitRate: partNo ? row.unitRate : "",
        };
      })
    );
  }

  function addRow() {
    setRows((current) => [...current, buildBlankWorkingRow(quotationType)]);
  }

  function removeRow(rowId) {
    setRows((current) => (current.length > 1 ? current.filter((row) => row.id !== rowId) : current));
  }

  const preparedRows = rows.map((row) => computePreparedWorkingRow(row, quotationType));

  const totals = computeWorkingTotals(preparedRows, quotationType);

  return (
    <div className="space-y-3">
      {!editable && (
        <div className="flex items-start gap-2.5 rounded-[12px] border border-[#e4eaf4] bg-[#f8fbff] px-3 py-2.5 text-[#6a7f9f]">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-[12px] font-semibold">{sq("readonlyTitle")}</p>
            <p className="mt-0.5 text-[11px] leading-5">{sq("readonlyHint")}</p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-[12px] border border-[#d8e0ed] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)]">
        <div className="flex items-center justify-between border-b border-[#d9e1ee] px-4 py-2.5 sm:px-5">
          <div>
            <p className="text-[13px] font-semibold text-[#2d394c]">{sq("workingSheet")}</p>
            <p className="mt-0.5 text-[11px] text-[#8ea0bd]">{sq("workingSheetHint")}</p>
            {makesError ? <p className="mt-1 text-[11px] text-[#b35b62]">{makesError}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            {editable && onSave ? (
              <QuotationSecondaryButton icon={Save} onClick={onSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </QuotationSecondaryButton>
            ) : null}
            <QuotationSecondaryButton icon={Plus} onClick={addRow} disabled={!editable || saving}>
              {sq("addRow")}
            </QuotationSecondaryButton>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[2120px] border-collapse">
            <thead className="sticky top-0 bg-[#f7faff]">
              <tr className="border-b border-[#d9e1ee] text-left">
                {workingColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-2.5 py-3 text-[10px] font-bold uppercase tracking-[0.04em] text-[#96a7c2] ${column.cellClassName || ""} ${
                      column.key === "index" ? "sticky left-0 z-[3] bg-[#f7faff]" : ""
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
                <th className="w-[62px] px-2.5 py-3 text-[10px] font-bold uppercase tracking-[0.04em] text-[#96a7c2]">{sq("action")}</th>
              </tr>
            </thead>
            <tbody>
              {preparedRows.map((row, index) => (
                <tr key={row.id} className="border-b border-[#edf1f7] last:border-b-0">
                  {workingColumns.map((column) => {
                    const calculatedValue = column.key === "index"
                      ? index + 1
                      : column.key === "initialAmount"
                        ? row.initialAmount
                        : column.key === "inputInstallationAmount"
                          ? row.inputInstallationAmount
                        : column.key === "amount"
                          ? row.amount
                          : column.key === "sellingInstallationRate"
                            ? row.sellingInstallationRate
                          : column.key === "installationAmount"
                            ? row.installationAmount
                            : row[column.key];

                    if (column.readonly) {
                      return (
                        <td
                          key={column.key}
                          className={`px-3 py-0 ${column.cellClassName || ""} ${
                            column.key === "index" ? "sticky left-0 z-[2] bg-white" : ""
                          }`}
                        >
                          <div className={`flex min-h-[44px] items-center justify-center px-2.5 py-2.5 text-center text-[11px] ${column.warning && !String(calculatedValue || "").trim()
                            ? "bg-[#fff8f1] text-[#ca7b30]"
                            : getCellTone(column)}`}>
                            {String(calculatedValue || "").trim() ? calculatedValue : column.warning ? sq("noCataloguePrice") : "-"}
                          </div>
                        </td>
                      );
                    }

                    if (column.key === "make") {
                      return (
                        <td key={column.key} className={`px-3 py-0 ${column.cellClassName || ""}`}>
                          <WorkingSheetMakeField
                            value={row.make}
                            makes={makes}
                            makesLoading={makesLoading}
                            editable={editable}
                            onChange={(makeName) => updateBrand(row.id, makeName)}
                          />
                        </td>
                      );
                    }

                    if (column.key === "partNo") {
                      return (
                        <td key={column.key} className={`px-3 py-0 ${column.cellClassName || ""}`}>
                          <WorkingSheetPartNoField
                            value={row.partNo}
                            make={row.make}
                            editable={editable}
                            onChange={(partNo) => updatePartNo(row.id, partNo)}
                            onProductSelect={(product) => applyProductToRow(row.id, product)}
                          />
                        </td>
                      );
                    }

                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-0 ${column.cellClassName || ""} ${
                          column.key === "index" ? "sticky left-0 z-[2] bg-white" : ""
                        }`}
                      >
                        <div className={`flex min-h-[44px] items-center px-2.5 py-2 ${getCellTone(column)}`}>
                          <input
                            value={row[column.key] ?? ""}
                            onChange={(event) => updateRow(row.id, column.key, event.target.value)}
                            disabled={!editable || saving}
                            inputMode={column.numeric ? "decimal" : "text"}
                            {...localizedFieldProps()}
                            className={`w-full border-0 bg-transparent text-[11px] outline-none transition disabled:cursor-not-allowed disabled:text-[#8ea0bd] ${
                              column.numeric ? "text-center" : "text-left leading-5"
                            }`}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-0">
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={!editable || preparedRows.length === 1 || saving}
                      className="grid min-h-[44px] w-full place-items-center text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#c8d2e1]"
                      aria-label="Remove row"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t border-[#d9e1ee] bg-[#f8fbff]">
                <td className="sticky left-0 z-[2] bg-[#f8fbff] px-3 py-3 text-[11px] font-bold uppercase tracking-[0.05em] text-[#6f82a1]">
                  {sq("total")}
                </td>
                {workingColumns.slice(1).map((column) => {
                  const totalValue =
                    column.key === "initialAmount" ? formatCurrency(totals.supplyInputAmount)
                      : column.key === "amount" ? formatCurrency(totals.supplySubtotal)
                      : column.key === "inputInstallationAmount" ? formatCurrency(totals.inputInstallationAmount)
                      : column.key === "installationAmount" ? formatCurrency(totals.installationSubtotal)
                      : null;

                  return (
                    <td key={`total-${column.key}`} className={`px-3 py-3 text-center text-[11px] font-semibold ${column.cellClassName || ""} ${column.accent ? "text-[#2944a8]" : "text-[#6f82a1]"}`}>
                      {totalValue || "-"}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-[11px] font-semibold text-[#6f82a1]">-</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border-t border-[#d9e1ee] bg-[#f6f9fd]">
          <div className={`grid gap-3 px-5 py-4 ${showInstallation ? "sm:grid-cols-3" : "sm:grid-cols-2"} sm:px-6`}>
            <div className="rounded-[12px] bg-white/65 px-4 py-3 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("supplySubtotal")}</p>
              <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrency(totals.supplySubtotal)}</p>
            </div>
            {showInstallation && (
              <div className="rounded-[12px] bg-white/65 px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("installationSubtotal")}</p>
                <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrency(totals.installationSubtotal)}</p>
              </div>
            )}
            <div className="rounded-[12px] bg-white px-4 py-3 text-center shadow-[0_10px_24px_-22px_rgba(30,51,131,0.22)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#2944a8]">{sq("customerSellingTotal")}</p>
              <p className="mt-1.5 text-[18px] font-semibold text-[#2944a8]">{formatCurrency(totals.customerSellingTotal)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function computeDerivedMultiplier(calculatedSellingPrice, supplyTotal, installationTotal) {
  const supplyInstallTotal = Number(supplyTotal || 0) + Number(installationTotal || 0);
  if (!(supplyInstallTotal > 0) || !Number.isFinite(calculatedSellingPrice)) return 0;
  return calculatedSellingPrice / supplyInstallTotal;
}

function CostPricingTab({
  quotationType,
  rows,
  setRows,
  editable,
  pmcEntries = [],
  setPmcEntries,
  hasPmcConfigs = false,
  overheadsPercent = "0",
  setOverheadsPercent,
  markUpPercent = "0",
  setMarkUpPercent,
  netPricePercentage = 0,
}) {
  const preparedRows = rows.map((row) => computePreparedWorkingRow(row, quotationType));
  const totals = computeWorkingTotals(preparedRows, quotationType);
  const normalizedPmcEntries = pmcEntries.map((entry, index) => normalizePmcEntry(entry, index));
  const pmcGroups = groupPmcEntriesForDisplay(normalizedPmcEntries);
  const pmcTotal = pmcGroups.reduce((sum, head) => sum + Number(head.totalCost || 0), 0);
  // Cost basis = Working Sheet Initial/Input amounts (not selling amounts), per PRD.
  const supplyCost = totals.supplyInputAmount;
  const installationCost = quotationType === "SITC" ? totals.inputInstallationAmount : 0;
  const supplyInstallCost = supplyCost + installationCost;
  const totalCost = supplyInstallCost + pmcTotal;
  const overheadsAmount = totalCost * (parseCurrency(overheadsPercent) / 100);
  const netMarginAmount = totalCost * (parseCurrency(markUpPercent) / 100);
  const grossMarginAmount = overheadsAmount + netMarginAmount;
  const calculatedSellingPrice = totalCost + grossMarginAmount;
  const realizedMarginPercent = totals.customerSellingTotal > 0
    ? ((totals.customerSellingTotal - totalCost) / totals.customerSellingTotal) * 100
    : 0;
  const targetMargin = Number(netPricePercentage) || 0;
  const showMarginWarning = targetMargin > 0 && realizedMarginPercent < targetMargin;
  const derivedMultiplier = computeDerivedMultiplier(
    calculatedSellingPrice,
    supplyCost,
    installationCost
  );
  const seededMultiplier = derivedMultiplier > 0 ? formatInputNumber(Number(derivedMultiplier.toFixed(4))) : "";

  useEffect(() => {
    if (!editable || !setRows || !seededMultiplier) return;

    setRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        const currentMultiplier = formatInputNumber(parseCurrency(row.multiplier || 0));
        if (currentMultiplier === seededMultiplier) return row;
        changed = true;
        return {
          ...row,
          multiplier: seededMultiplier,
          unitRate: "",
        };
      });
      return changed ? next : current;
    });
  }, [
    editable,
    setRows,
    seededMultiplier,
    overheadsPercent,
    markUpPercent,
    pmcTotal,
    supplyCost,
    installationCost,
  ]);

  const costRows = [
    { head: "Supply", amount: formatCurrency(supplyCost), percent: `${totalCost ? ((supplyCost / totalCost) * 100).toFixed(1) : "0.0"}%` },
    { head: "Installation", amount: formatCurrency(installationCost), percent: `${totalCost ? ((installationCost / totalCost) * 100).toFixed(1) : "0.0"}%` },
    { head: "Total", amount: formatCurrency(supplyInstallCost), percent: `${totalCost ? ((supplyInstallCost / totalCost) * 100).toFixed(0) : "0"}%`, emphasis: true },
    ...(hasPmcConfigs
      ? [
          ...pmcGroups.map((head) => ({ head: head.title, amount: formatCurrency(head.totalCost), percent: `${totalCost ? ((head.totalCost / totalCost) * 100).toFixed(0) : "0"}%` })),
          { head: "Total PMC Cost", amount: formatCurrency(pmcTotal), percent: `${totalCost ? ((pmcTotal / totalCost) * 100).toFixed(0) : "0"}%`, emphasis: true },
        ]
      : []),
    { head: "Total Cost", amount: formatCurrency(totalCost), percent: "100%", grand: true },
  ];

  function updatePmcEntry(entryId, fieldKey, value) {
    if (!setPmcEntries) return;
    setPmcEntries((current) => patchPmcEntryValue(current, entryId, fieldKey, value));
  }

  function renderPmcCell(row, column) {
    const rawValue = column.key === "rate" || column.key === "unitRate"
      ? (row.rate ?? row.unitRate ?? "")
      : (row[column.key] ?? "");
    const value = rawValue === 0 ? "0" : String(rawValue ?? "");

    if (column.key === "totalCost") {
      return (
        <span className="block text-right text-[12px] font-semibold text-[#2d394c]">
          {formatCurrency(row.totalCost)}
        </span>
      );
    }
    if (column.key === "description") {
      return (
        <span className="block text-left text-[12px] font-medium text-[#2d394c]">
          {row.description || "-"}
        </span>
      );
    }
    if (!editable || column.readonly || !setPmcEntries) {
      return (
        <span className={`block text-[12px] text-[#2d394c] ${column.align === "left" ? "text-left" : "text-right"}`}>
          {value || "-"}
        </span>
      );
    }
    return (
      <input
        value={value}
        onChange={(event) => updatePmcEntry(row.id, column.key, event.target.value)}
        disabled={!editable}
        inputMode="decimal"
        {...localizedFieldProps()}
        className={`h-7 w-full min-w-[56px] rounded-[6px] border border-transparent bg-transparent px-1.5 text-[12px] text-[#2d394c] outline-none transition hover:border-[#d5deec] focus:border-[#9fb4dd] focus:bg-white disabled:text-[#6f82a1] ${column.align === "left" ? "text-left" : "text-right"}`}
      />
    );
  }

  return (
    <section className="grid gap-3 xl:grid-cols-[1.35fr_1fr]">
      <div className="space-y-3">
        <SectionShell compact title="Cost Details" action={<span className="rounded-[10px] bg-[#fff2c9] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-[#e28a00]">Internal only</span>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] border-collapse">
              <thead>
                <tr className="border-b border-[#d9e1ee] bg-[#f8fbff] text-left">
                  {["Head", "Amount", "% of Cost"].map((heading) => (
                    <th key={heading} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.05em] text-[#96a7c2]">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costRows.map((row) => (
                  <tr key={row.head} className="border-b border-[#edf1f7] last:border-b-0">
                    <td className={`px-3 py-2 text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#2d394c]"}`}>{row.head}</td>
                    <td className={`px-3 py-2 text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#6f82a1]"}`}>{row.amount}</td>
                    <td className={`px-3 py-2 text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#6f82a1]"}`}>{row.percent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionShell>

        {hasPmcConfigs && pmcGroups.length > 0 && (
          <div className="space-y-3">
            {pmcGroups.map((head) => (
              <div
                key={head.id}
                className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)]"
              >
                <div className="flex items-center justify-between border-b border-[#d9e1ee] px-4 py-3 sm:px-5">
                  <h3 className="text-[13px] font-semibold tracking-[-0.01em] text-[#2d394c]">{head.title}</h3>
                  <span className="text-[13px] font-semibold text-[#23324c]">{formatCurrency(head.totalCost)}</span>
                </div>
                {head.columns.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] border-collapse">
                      <thead>
                        <tr className="border-b border-[#d9e1ee] bg-[#f8fbff]">
                          {head.columns.map((column) => (
                            <th
                              key={column.reactKey || column.id || column.key}
                              className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#96a7c2] ${column.align === "left" ? "text-left" : "text-right"}`}
                            >
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {head.rows.map((row, index) => (
                          <tr key={`${head.id}-${row.id || index}`} className="border-b border-[#edf1f7] last:border-b-0">
                            {head.columns.map((column) => (
                              <td
                                key={`${head.id}-${row.id || index}-${column.reactKey || column.id || column.key}`}
                                className={`px-4 py-2 ${column.align === "left" ? "text-left" : "text-right"}`}
                              >
                                {renderPmcCell(row, column)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-4 py-3 text-[12px] text-[#7a8da8]">
                    Column definitions were not returned by the PMC configuration API for this cost head.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <SectionShell compact title="Selling Details">
          <div className="px-3 py-2.5 sm:px-4">
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Overheads</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <input
                    value={overheadsPercent}
                    onChange={(event) => setOverheadsPercent?.(event.target.value)}
                    disabled={!editable}
                    {...localizedFieldProps()}
                    className="flex h-8 min-w-[72px] items-center justify-end rounded-[10px] border border-[#cdd8ea] bg-white px-3 text-right text-[12px] text-[#5c6f90] outline-none disabled:bg-[#fbfcfe]"
                  />
                  <span className="text-[12px] text-[#94a5bf]">%</span>
                </div>
                <p className="min-w-[100px] text-right text-[13px] font-semibold text-[#23324c]">{formatCurrency(overheadsAmount)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Mark Up</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <input
                    value={markUpPercent}
                    onChange={(event) => setMarkUpPercent?.(event.target.value)}
                    disabled={!editable}
                    {...localizedFieldProps()}
                    className="flex h-8 min-w-[72px] items-center justify-end rounded-[10px] border border-[#cdd8ea] bg-white px-3 text-right text-[12px] text-[#5c6f90] outline-none disabled:bg-[#fbfcfe]"
                  />
                  <span className="text-[12px] text-[#94a5bf]">%</span>
                </div>
                <p className="min-w-[100px] text-right text-[13px] font-semibold text-[#94a5bf]">-</p>
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Net Margin</p>
              <p className="text-[13px] font-semibold text-[#23324c]">{formatCurrency(netMarginAmount)}</p>
            </div>
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Gross Margin</p>
              <p className="text-[13px] font-semibold text-[#23324c]">{formatCurrency(grossMarginAmount)}</p>
            </div>
            <div className="flex items-center justify-between pt-3">
              <p className="text-[13px] font-semibold text-[#2d394c]">Calculated Selling Price</p>
              <p className="text-[18px] font-semibold tracking-[-0.03em] text-[#2944a8]">{formatCurrency(calculatedSellingPrice)}</p>
            </div>
          </div>
        </SectionShell>

        <SectionShell compact title="Realized Pricing Summary">
          <div className="px-3 py-2.5 sm:px-4">
            <SummaryRow
              label="Derived multiplier"
              helper="seeded to Working Sheet Mult."
              value={derivedMultiplier > 0 ? `${derivedMultiplier.toFixed(2)}x` : "-"}
            />
            <SummaryRow label="Realized selling" helper="from Working Sheet" value={formatCurrency(totals.customerSellingTotal)} />
            <SummaryRow
              label="Realized margin"
              value={`${realizedMarginPercent.toFixed(1)}%`}
              highlight={showMarginWarning}
            />
            {showMarginWarning && (
              <div className="mt-2 rounded-[10px] bg-[#fff3cd] px-3 py-2.5 text-[12px] leading-5 text-[#d47700]">
                Realized margin is below the tenant target of {targetMargin}%. This warning should stay visible during manager review.
              </div>
            )}
          </div>
        </SectionShell>
      </div>
    </section>
  );
}

function SummaryRow({ label, helper, value, highlight = false }) {
  return (
    <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <p className="text-[12px] text-[#6f82a1]">{label}</p>
        {helper && <span className="text-[10px] text-[#9aacbf]">({helper})</span>}
      </div>
      {highlight ? (
        <span className="rounded-full bg-[#fde8e5] px-3 py-1 text-[12px] font-semibold text-[#ca3c2f]">{value}</span>
      ) : (
        <p className="text-[13px] font-semibold text-[#23324c]">{value}</p>
      )}
    </div>
  );
}

function TermsAndConditionsTab({
  standardTerms = [],
  selectedStandardTerms,
  setSelectedStandardTerms,
  additionalTermEntries,
  setAdditionalTermEntries,
  previewOrder,
  setPreviewOrder,
  editable,
  onSave,
  saving,
}) {
  const [draftAdditionalTerms, setDraftAdditionalTerms] = useState(() =>
    (Array.isArray(additionalTermEntries) ? additionalTermEntries : []).map((entry, index) =>
      normalizeAdditionalTermEntry(entry, index)
    )
  );

  useEffect(() => {
    setDraftAdditionalTerms(
      (Array.isArray(additionalTermEntries) ? additionalTermEntries : []).map((entry, index) =>
        normalizeAdditionalTermEntry(entry, index)
      )
    );
  }, [additionalTermEntries]);

  const previewTerms = useMemo(
    () => getVisibleTerms(standardTerms, selectedStandardTerms, additionalTermEntries, previewOrder),
    [standardTerms, selectedStandardTerms, additionalTermEntries, previewOrder]
  );

  useEffect(() => {
    const ids = getVisibleTerms(standardTerms, selectedStandardTerms, additionalTermEntries, []).map((item) => item.id);
    setPreviewOrder((current) => {
      const existing = (current || []).filter((id) => ids.includes(id));
      const missing = ids.filter((id) => !existing.includes(id));
      const next = [...existing, ...missing];
      if (
        next.length === (current || []).length &&
        next.every((id, index) => id === current[index])
      ) {
        return current;
      }
      return next;
    });
  }, [standardTerms, selectedStandardTerms, additionalTermEntries, setPreviewOrder]);

  function syncAdditionalTermsToParent(entries) {
    setAdditionalTermEntries(
      entries.map((entry, index) => normalizeAdditionalTermEntry(entry, index))
    );
  }

  function moveTerm(index, direction) {
    setPreviewOrder((current) => {
      const next = [...current];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return current;
      const swap = next[index];
      next[index] = next[target];
      next[target] = swap;
      return next;
    });
  }

  function toggleStandardTerm(id) {
    setSelectedStandardTerms((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  }

  function patchAdditionalTerm(index, field, value) {
    setDraftAdditionalTerms((current) => {
      const next = current.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      syncAdditionalTermsToParent(next);
      return next;
    });
  }

  function addAdditionalTerm() {
    setDraftAdditionalTerms((current) => {
      const next = [...current, createAdditionalTermEntry(current.length)];
      syncAdditionalTermsToParent(next);
      return next;
    });
  }

  function removeAdditionalTerm(index) {
    setDraftAdditionalTerms((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      syncAdditionalTermsToParent(next);
      return next;
    });
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-end">
        {editable && onSave ? (
          <QuotationSecondaryButton icon={Save} onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save terms"}
          </QuotationSecondaryButton>
        ) : null}
      </div>

      <div className="grid gap-3 xl:grid-cols-[1fr_1fr]">
        <SectionShell compact title="Standard terms">
          <div className="space-y-1.5 px-3 py-3 sm:px-4">
            {standardTerms.length === 0 ? (
              <div className="rounded-[10px] border border-dashed border-[#d8e0ed] bg-[#fafcff] px-3 py-3.5 text-[11px] text-[#8fa0ba]">
                No standard terms are currently configured. You can still add quotation-specific terms below.
              </div>
            ) : (
              standardTerms.map((term) => {
                const selected = selectedStandardTerms.includes(term.id);
                return (
                  <button
                    key={term.id}
                    type="button"
                    onClick={() => toggleStandardTerm(term.id)}
                    disabled={!editable}
                    className={`block w-full rounded-[10px] border px-3 py-2.5 text-left transition ${
                      selected ? "border-[#dde4f2] bg-[#f2f5ff]" : "border-[#edf1f7] bg-white hover:border-[#d8e0ed] hover:bg-[#fafcff]"
                    } disabled:cursor-not-allowed disabled:opacity-80`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`mt-0.5 grid h-4 w-4 place-items-center rounded-md border ${selected ? "border-[#2944a8] bg-[#2944a8] text-white" : "border-[#c5cfde] bg-white text-transparent"}`}>
                        <Check className="h-3 w-3" />
                      </div>
                      <div>
                        <p className="text-[12px] font-semibold text-[#2d394c]">{term.title}</p>
                        <p className="mt-1 text-[11px] leading-5 text-[#6f82a1]">{term.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </SectionShell>

        <SectionShell compact title="Selected terms preview" action={<Eye className="h-4 w-4 text-[#6f82a1]" />}>
          <div className="px-3 py-3 sm:px-4">
            {previewTerms.length === 0 ? (
              <div className="py-4 text-[11px] text-[#8fa0ba]">Select standard terms or add quotation-specific terms to build the final PDF order.</div>
            ) : (
              previewTerms.map((term, index) => (
                <div key={term.id} className={`flex items-start gap-2.5 py-2.5 ${index < previewTerms.length - 1 ? "border-b border-[#edf1f7]" : ""}`}>
                  <div className="pt-0.5 text-[#96a7c2]">
                    <GripVertical className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[12px] font-semibold text-[#2d394c]">{index + 1}. {term.title}</p>
                      <div className="flex items-center gap-1.5">
                        <button type="button" onClick={() => moveTerm(index, "up")} disabled={!editable || index === 0} className="grid h-7 w-7 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] disabled:opacity-40">
                          <MoveUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => moveTerm(index, "down")} disabled={!editable || index === previewTerms.length - 1} className="grid h-7 w-7 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] disabled:opacity-40">
                          <MoveDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-0.5 text-[11px] leading-5 text-[#6f82a1]">{term.description}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </SectionShell>
      </div>

      <SectionShell compact title="Additional terms">
        <div className="px-3 pb-3 pt-2.5 sm:px-4">
          <div className="space-y-2">
            {draftAdditionalTerms.length === 0 ? (
              <div className="rounded-[10px] border border-dashed border-[#d8e0ed] bg-[#fafcff] px-3 py-3.5 text-[11px] text-[#8fa0ba]">
                Add quotation-specific terms below. Each term will appear as its own item in the preview once text is entered.
              </div>
            ) : (
              draftAdditionalTerms.map((term, index) => (
                <div
                  key={term.id || `additional-term-${index}`}
                  className="rounded-[10px] border border-[#edf1f7] bg-white px-3 py-2.5 transition hover:border-[#d8e0ed] hover:bg-[#fafcff]"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 grid h-4 w-4 place-items-center rounded-md border border-[#2944a8] bg-[#2944a8] text-white">
                      <Check className="h-3 w-3" />
                    </div>
                    <div className="flex-1">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        {editable ? (
                          <input
                            value={term.title}
                            onChange={(event) => patchAdditionalTerm(index, "title", event.target.value)}
                            className="w-full rounded-[10px] border border-[#cdd8ea] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#2d394c] outline-none transition focus:border-[#9fb4dd]"
                            placeholder={`Additional term ${index + 1}`}
                            {...localizedFieldProps()}
                          />
                        ) : (
                          <p className="text-[12px] font-semibold text-[#2d394c]">
                            {getAdditionalTermDisplayTitle(term, index)}
                          </p>
                        )}
                        {editable && (
                          <button
                            type="button"
                            onClick={() => removeAdditionalTerm(index)}
                            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] transition hover:bg-[#f7faff] hover:text-[#2944a8]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <Textarea
                        value={term.description}
                        onChange={(event) => patchAdditionalTerm(index, "description", event.target.value)}
                        readOnly={!editable}
                        className="min-h-[80px] rounded-[10px] border-[#cdd8ea] px-3 py-2 text-[12px] leading-5 text-[#2d394c] placeholder:text-[#8e9cb2] read-only:bg-[#fbfcfe] read-only:cursor-not-allowed"
                        placeholder="Add quotation-specific term text"
                        {...localizedFieldProps()}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {editable && (
              <button
                type="button"
                onClick={addAdditionalTerm}
                className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[12px] font-semibold text-[#2944a8] transition hover:bg-[#f7faff] hover:text-[#1d327f]"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add term</span>
              </button>
            )}
          </div>
        </div>
      </SectionShell>
    </div>
  );
}

function VersionHistoryDrawer({ open, versions, currentVersion, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-[380px] border-l border-[#d8e0ed] bg-white shadow-[-16px_0_32px_-24px_rgba(30,51,131,0.28)]">
        <div className="flex items-center justify-between border-b border-[#d9e1ee] px-4 py-3">
          <p className="text-[14px] font-semibold text-[#23324c]">Version History</p>
          <button type="button" onClick={onClose} className="rounded-lg border border-[#d8e0ed] px-2.5 py-1 text-[11px] font-semibold text-[#5d7598]">
            Close
          </button>
        </div>
        <div className="space-y-2 px-3 py-3">
          {versions.map((version) => (
            <div
              key={`${version.refId}-${version.version}`}
              className={`rounded-[10px] border px-3 py-2.5 ${version.version === currentVersion ? "border-[#c6d4f0] bg-[#f7faff]" : "border-[#e3e9f3] bg-white"}`}
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                <span className="font-semibold text-[#23324c]">{version.versionLabel}</span>
                <span className="text-[#c5d0e0]">·</span>
                <span className="text-[#7d8fa9]">Updated {version.updatedLabel}</span>
                <span className="text-[#c5d0e0]">·</span>
                <StatusPill tone={version.statusTone} label={version.status} />
                {version.superseded && <StatusPill tone="superseded" label="Superseded" />}
                {version.version === currentVersion && <StatusPill tone="approved" label="Current" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ManagerSummaryCard({ quotation, rows, pmcEntries = [] }) {
  const totals = computeWorkingTotals(rows, quotation.type);
  const pmcTotal = pmcEntries.reduce((sum, entry) => sum + Number(entry.totalCost || 0), 0);
  const supplyCost = totals.supplyInputAmount;
  const installationCost = quotation.type === "SITC" ? totals.inputInstallationAmount : 0;
  const totalCost = supplyCost + installationCost + pmcTotal;
  const overheadsAmount = totalCost * ((parseCurrency(quotation.overheadsPct) || 0) / 100);
  const netMarginAmount = totalCost * ((parseCurrency(quotation.markUpPct) || 0) / 100);
  const grossMarginAmount = overheadsAmount + netMarginAmount;
  const calculatedSellingPrice = totalCost + grossMarginAmount;
  const realizedMargin = totals.customerSellingTotal - totalCost;
  const realizedMarginPercent = totals.customerSellingTotal > 0 ? (realizedMargin / totals.customerSellingTotal) * 100 : 0;
  const configuredTargetMargin = Number(quotation.netPricePercentage ?? quotation.markUpPct ?? 0) || 0;
  const marginBelowTarget = configuredTargetMargin > 0 && realizedMarginPercent < configuredTargetMargin;

  return (
    <div className="space-y-5">
      <SectionShell title="Approval summary" action={<StatusPill tone={quotation.statusTone} label={quotation.status} />}>
        <div className="grid gap-y-6 px-5 py-5 sm:grid-cols-2 sm:gap-x-10 sm:px-6 lg:grid-cols-3">
          {[
            ["Customer", quotation.customer],
            ["Ref ID", quotation.refId],
            ["Quotation Type", quotation.type],
            ["Version", quotation.versionLabel],
            ["Total Cost", formatCurrency(totalCost)],
            ["Gross Margin", formatCurrency(grossMarginAmount)],
            ["Net Margin", formatCurrency(netMarginAmount)],
            ["Calculated Selling Price", formatCurrency(calculatedSellingPrice)],
            ["Working Sheet Customer Selling Total", formatCurrency(totals.customerSellingTotal)],
            ["Realized Margin", `${realizedMarginPercent.toFixed(1)}%`],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#96a7c2]">{label}</p>
              <p className="text-[14px] font-medium leading-6 text-[#2d394c]">{value}</p>
            </div>
          ))}
        </div>
      </SectionShell>

      {marginBelowTarget && (
        <div className="rounded-[14px] border border-[#f0d0a7] bg-[#fff8ed] px-5 py-4 text-[#b06c16] shadow-[0_12px_28px_-24px_rgba(176,108,22,0.35)]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="text-[13px] font-semibold">Margin warning</p>
              <p className="mt-1 text-[12px] leading-6">
                Realized margin is {realizedMarginPercent.toFixed(1)}%, below the configured target margin. The manager can still review and decide.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManagerDecisionBar({ quotation, onApprove, onOpenRevision, decided }) {
  if (quotation.status !== "Pending Approval" || decided) return null;

  return (
    <div className="sticky bottom-0 z-20 mt-6 rounded-[14px] border border-[#d8e0ed] bg-white/95 px-5 py-4 shadow-[0_-10px_28px_-20px_rgba(30,51,131,0.32)] backdrop-blur">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[14px] font-semibold text-[#23324c]">Approval decision</p>
          <p className="mt-1 text-[12px] text-[#7c8faa]">Choose one of the two approval outcomes for this submitted quotation.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <QuotationPrimaryButton icon={Check} onClick={onApprove}>
            Approve
          </QuotationPrimaryButton>
          <QuotationSecondaryButton icon={RotateCcw} onClick={onOpenRevision}>
            Return for Revision
          </QuotationSecondaryButton>
        </div>
      </div>
    </div>
  );
}

function ReturnRevisionModal({ open, note, setNote, onConfirm, onCancel }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130]">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={onCancel} />
      <div className="absolute left-1/2 top-1/2 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
        <div className="border-b border-[#d9e1ee] px-5 py-4 sm:px-6">
          <p className="text-[18px] font-semibold text-[#23324c]">Return for Revision</p>
          <p className="mt-1 text-[12px] text-[#7c8faa]">Add a revision note for the submitter before confirming this action.</p>
        </div>
        <div className="px-5 py-5 sm:px-6">
          <label className="mb-2 block text-[12px] font-semibold text-[#51657f]">Revision note</label>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="min-h-[160px] rounded-[14px] border-[#cdd8ea] px-5 py-4 text-[13px] text-[#2d394c] placeholder:text-[#8e9cb2]"
            placeholder="Explain what needs to change before this quotation can be approved..."
          />
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-[#d9e1ee] px-5 py-4 sm:px-6">
          <QuotationSecondaryButton icon={ArrowLeft} onClick={onCancel}>
            Cancel
          </QuotationSecondaryButton>
          <QuotationPrimaryButton icon={RotateCcw} onClick={onConfirm} disabled={!note.trim()}>
            Confirm action
          </QuotationPrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ManagerQuotationReview({ baseQuotation, baseHistory }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("workingSheet");
  const [quotation, setQuotation] = useState(baseQuotation);
  const [pmcEntries, setPmcEntries] = useState([]);
  const [hasPmcConfigs, setHasPmcConfigs] = useState(false);
  const [additionalTermEntries, setAdditionalTermEntries] = useState([]);
  const [standardTerms, setStandardTerms] = useState([]);
  const [selectedStandardTerms, setSelectedStandardTerms] = useState([]);
  const [previewOrder, setPreviewOrder] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [decisionDone, setDecisionDone] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    setQuotation(baseQuotation);
    setDecisionDone(false);
    setActionError("");
  }, [baseQuotation]);

  useEffect(() => {
    let active = true;

    resolvePmcEntriesForQuotation(baseQuotation.type, baseQuotation.pmcs || [])
      .then(({ entries, hasPmcConfigs: hasConfigs }) => {
        if (!active) return;
        setHasPmcConfigs(hasConfigs);
        setPmcEntries(entries);
      })
      .catch(() => {
        if (!active) return;
        setHasPmcConfigs(false);
        setPmcEntries([]);
      });

    return () => {
      active = false;
    };
  }, [baseQuotation.id, baseQuotation.type, baseQuotation.pmcs]);

  useEffect(() => {
    let active = true;

    getTerms()
      .then((response) => {
        if (!active) return;
        const nextTerms = extractApiTerms(response?.data)
          .sort((a, b) => Number(a.order) - Number(b.order))
          .map((term, index) => ({
            ...term,
            id: term.id ?? `term-${index + 1}`,
            description: term.text || term.description || "",
          }))
          .filter((term) => !term.status || term.status === "Active");
        const resolved = resolveTermsFromQuotation(baseQuotation.specificTerms || [], nextTerms);
        setStandardTerms(nextTerms);
        setSelectedStandardTerms(resolved.selectedIds);
        setAdditionalTermEntries(resolved.additionalTermEntries);
        setPreviewOrder(resolved.previewOrder);
      })
      .catch(() => {
        if (!active) return;
        setStandardTerms([]);
        setSelectedStandardTerms([]);
        setAdditionalTermEntries([]);
        setPreviewOrder([]);
      });

    return () => {
      active = false;
    };
  }, [baseQuotation]);

  async function approveQuotation() {
    setActionBusy(true);
    setActionError("");

    try {
      if (!quotation.id) throw new Error("Quotation ID is missing.");

      const reviewResponse = await reviewQuotation(quotation.id, {
        action: "APPROVE",
        managerNote: revisionNote.trim(),
      });
      const reviewedPayload =
        extractObjectPayload(reviewResponse?.data) ||
        extractObjectPayload(reviewResponse) ||
        null;

      let next = reviewedPayload ? normalizeApiQuotation(reviewedPayload) : null;
      if (!next?.refId || next.status !== "Approved") {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation(latest?.data || {});
      }

      if (!next.refId) {
        throw new Error("Backend did not return the updated quotation after approval.");
      }
      if (next.status !== "Approved") {
        throw new Error(`Approval completed, but status is still "${next.status}". Expected Approved.`);
      }

      setQuotation(next);
      setDecisionDone(true);
    } catch (error) {
      setActionError(error.message || "Failed to approve quotation.");
    } finally {
      setActionBusy(false);
    }
  }

  async function confirmRevision() {
    setActionBusy(true);
    setActionError("");

    try {
      if (!quotation.id) throw new Error("Quotation ID is missing.");
      const note = revisionNote.trim();
      if (!note) throw new Error("A revision note is required.");

      const reviewResponse = await reviewQuotation(quotation.id, {
        action: "REJECT",
        managerNote: note,
      });
      const reviewedPayload =
        extractObjectPayload(reviewResponse?.data) ||
        extractObjectPayload(reviewResponse) ||
        null;

      let next = reviewedPayload
        ? normalizeApiQuotation({ ...reviewedPayload, managerNote: reviewedPayload.managerNote || note })
        : null;

      if (!next?.refId || next.status !== "Draft") {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation({
          ...(latest?.data || {}),
          managerNote: note,
        });
      }

      if (!next.refId) {
        throw new Error("Backend did not return the updated quotation after revision.");
      }

      // Return-for-revision must reopen the same version as Draft (never create a new version).
      if (next.status !== "Draft") {
        next = hydrateQuotation({
          ...next,
          status: "Draft",
          statusTone: statusTone("Draft"),
          revisionNote: note,
          submissionNote: "",
        });
      } else if (!next.revisionNote) {
        next = hydrateQuotation({
          ...next,
          revisionNote: note,
          submissionNote: "",
        });
      }

      setQuotation(next);
      setDecisionDone(true);
      setShowRevisionModal(false);
      setRevisionNote("");
    } catch (error) {
      setActionError(error.message || "Failed to return quotation for revision.");
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="space-y-5 pb-6">
      <button
        type="button"
        className="inline-flex items-center gap-2.5 text-[14px] font-medium text-[#5d7598] transition hover:text-[#2944a8]"
        onClick={() => navigate("/sales/quotations/quotation")}
      >
        <ArrowLeft className="h-4.5 w-4.5" />
        <span>Back</span>
      </button>

      <div className="rounded-[14px] border border-[#d8e0ed] bg-white px-5 py-6 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] sm:px-7">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-[24px] font-semibold tracking-[0.01em] text-[#23324c]">{quotation.refId}</h2>
              <StatusPill tone={quotation.statusTone} label={quotation.status} />
              <span className="rounded-full bg-[#eef2f8] px-3 py-1.5 text-[12px] font-semibold text-[#647b9f]">{quotation.versionLabel}</span>
              {quotation.expired && <StatusPill tone="rejected" label="Expired" />}
            </div>
            <p className="text-[14px] leading-7 text-[#7384a2]">{quotation.customer} · {quotation.type} · {quotation.location}</p>
            <p className="text-[12px] text-[#8ea0bd]">This approval view is read-only while the quotation is pending review.</p>
          </div>
          <QuotationSecondaryButton icon={Clock3} onClick={() => setHistoryOpen(true)}>
            View Previous Versions
          </QuotationSecondaryButton>
        </div>
      </div>

      {actionError && <ErrorState message={actionError} onRetry={() => setActionError("")} />}

      {decisionDone && (
        <div className="rounded-[14px] border border-[#d5e8dc] bg-[#f4fff8] px-5 py-4 text-[#2b7a4b]">
          <p className="text-[13px] font-semibold">
            {quotation.status === "Approved"
              ? "Quotation approved successfully."
              : "Quotation returned for revision successfully."}
          </p>
          <p className="mt-1 text-[12px]">
            Status is now <span className="font-semibold">{quotation.status}</span>
            {quotation.status === "Draft" && quotation.revisionNote
              ? " and the revision note has been attached. The same version is editable again."
              : quotation.status === "Approved"
                ? ". Quotation remains locked and PDF download is available."
                : "."}
          </p>
        </div>
      )}

      {quotation.revisionNote && quotation.status === "Draft" && (
        <div className="rounded-[14px] border border-[#f0d0a7] bg-[#fff8ed] px-5 py-4 text-[#b06c16]">
          <p className="text-[13px] font-semibold">Revision note</p>
          <p className="mt-1 text-[12px] leading-6">{quotation.revisionNote}</p>
        </div>
      )}

      <ManagerSummaryCard quotation={quotation} rows={quotation.workingRows} pmcEntries={pmcEntries} />

      <div className="flex flex-wrap items-end gap-6 border-b border-[#d7dfec] px-1">
        {[
          { key: "workingSheet", label: "Working Sheet" },
          { key: "costPricing", label: "Cost + Pricing", badge: "INTERNAL" },
          { key: "terms", label: "Terms & Conditions" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`relative pb-5 text-[15px] font-semibold transition ${activeTab === tab.key ? "text-[#2944a8]" : "text-[#6f82a1]"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span>{tab.label}</span>
            {tab.badge && <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#ef8c16]">{tab.badge}</span>}
            {activeTab === tab.key && <span className="absolute inset-x-0 bottom-[-1px] h-[3px] rounded-full bg-[#2944a8]" />}
          </button>
        ))}
      </div>

      {activeTab === "workingSheet" && (
        <WorkingSheetTab quotationType={quotation.type} rows={quotation.workingRows} setRows={() => {}} editable={false} />
      )}
      {activeTab === "costPricing" && (
        <CostPricingTab
          quotationType={quotation.type}
          rows={quotation.workingRows}
          editable={false}
          pmcEntries={pmcEntries}
          hasPmcConfigs={hasPmcConfigs}
          overheadsPercent={quotation.overheadsPct}
          markUpPercent={quotation.markUpPct}
          netPricePercentage={quotation.netPricePercentage}
        />
      )}
      {activeTab === "terms" && (
        <TermsAndConditionsTab
          standardTerms={standardTerms}
          selectedStandardTerms={selectedStandardTerms}
          setSelectedStandardTerms={setSelectedStandardTerms}
          additionalTermEntries={additionalTermEntries}
          setAdditionalTermEntries={setAdditionalTermEntries}
          previewOrder={previewOrder}
          setPreviewOrder={setPreviewOrder}
          editable={false}
        />
      )}

      <ManagerDecisionBar
        quotation={quotation}
        onApprove={approveQuotation}
        onOpenRevision={() => setShowRevisionModal(true)}
        decided={decisionDone || actionBusy}
      />
      <VersionHistoryDrawer open={historyOpen} versions={baseHistory} currentVersion={quotation.version} onClose={() => setHistoryOpen(false)} />
      <ReturnRevisionModal
        open={showRevisionModal}
        note={revisionNote}
        setNote={setRevisionNote}
        onConfirm={confirmRevision}
        onCancel={() => {
          setShowRevisionModal(false);
          setRevisionNote("");
        }}
      />
    </div>
  );
}

function ConfigEmptyState({ title, body, actionLabel, onAction }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#d8e0ed] bg-[#fafcff] px-5 py-8 text-center">
      <p className="text-[15px] font-semibold text-[#23324c]">{title}</p>
      <p className="mt-2 text-[13px] leading-6 text-[#7c8faa]">{body}</p>
      {actionLabel && onAction && (
        <div className="mt-4">
          <QuotationSecondaryButton icon={Plus} onClick={onAction}>
            {actionLabel}
          </QuotationSecondaryButton>
        </div>
      )}
    </div>
  );
}

function HeadEditorModal({ open, value, onChange, onSave, onCancel, title, saving = false, hint = "" }) {
  if (!open) return null;

  function patchField(key, nextValue) {
    onChange((current) => ({ ...current, [key]: nextValue }));
  }

  function patchRow(index, nextValue) {
    onChange((current) => ({
      ...current,
      defaultRows: current.defaultRows.map((row, rowIndex) => (rowIndex === index ? nextValue : row)),
    }));
  }

  return (
    <div className="fixed inset-0 z-[130]">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={onCancel} />
      <div className="absolute left-1/2 top-1/2 w-[min(640px,calc(100vw-2rem))] max-h-[85vh] -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-[20px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
        <div className="border-b border-[#d9e1ee] px-5 py-4 sm:px-6">
          <p className="text-[17px] font-semibold text-[#23324c]">{title}</p>
          <p className="mt-1 text-[12px] text-[#7c8faa]">
            {hint || "Configure the cost head name and breakdown rows used later in Cost + Pricing."}
          </p>
        </div>
        <div className="space-y-4 px-5 py-5 sm:px-6">
          <FormField label="Cost head name" required>
            <input value={value.name} onChange={(event) => patchField("name", event.target.value)} className={inputClass(false)} {...localizedFieldProps()} />
          </FormField>

          <FormField label="Type / category">
            <input
              value={value.category}
              onChange={(event) => patchField("category", event.target.value)}
              placeholder="e.g. Consumables, Manpower"
              className={inputClass(false)}
              {...localizedFieldProps()}
            />
          </FormField>

          <div className="flex items-center justify-between gap-3 rounded-[12px] border border-[#e1e8f4] bg-[#fbfcff] px-3 py-2.5">
            <p className="text-[12px] font-semibold text-[#51657f]">Status</p>
            <button
              type="button"
              onClick={() => patchField("status", value.status === "Active" ? "Inactive" : "Active")}
              className={`rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] ${
                value.status === "Active" ? "bg-[#dff8e8] text-[#219653]" : "bg-[#eef2f8] text-[#7b8faa]"
              }`}
            >
              {value.status || "Active"}
            </button>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[12px] font-semibold text-[#51657f]">Breakdown rows</p>
              <button
                type="button"
                onClick={() => patchField("defaultRows", [...(value.defaultRows || []), ""])}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2944a8]"
              >
                <Plus className="h-3.5 w-3.5" />
                Add row
              </button>
            </div>
            <div className="space-y-2">
              {(value.defaultRows || []).map((row, index) => (
                <div key={`row-${index}`} className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 shrink-0 text-[#9aaac2]" />
                  <input
                    value={row}
                    onChange={(event) => patchRow(index, event.target.value)}
                    placeholder="Item"
                    className="min-h-[40px] flex-1 rounded-[12px] border border-[#cfd9ea] bg-white px-3 text-[13px] text-[#33425d] outline-none"
                    {...localizedFieldProps()}
                  />
                  <button
                    type="button"
                    onClick={() => patchField("defaultRows", value.defaultRows.filter((_, itemIndex) => itemIndex !== index))}
                    className="grid h-9 w-9 place-items-center rounded-lg text-[#8ea0bd] hover:bg-[#f1f5fb]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-[#d9e1ee] px-5 py-4 sm:px-6">
          <QuotationSecondaryButton icon={ArrowLeft} onClick={onCancel} disabled={saving}>
            Cancel
          </QuotationSecondaryButton>
          <QuotationPrimaryButton icon={Check} onClick={onSave} disabled={saving || !String(value.name || "").trim()}>
            {saving ? "Saving..." : "Save"}
          </QuotationPrimaryButton>
        </div>
      </div>
    </div>
  );
}

function QuotationConfigurationWorkspace({ activeSection }) {
  const [heads, setHeads] = useState([]);
  const [rules, setRules] = useState([]);
  const [terms, setTerms] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);
  const [approverOptionsLoading, setApproverOptionsLoading] = useState(false);
  const [approverOptionsError, setApproverOptionsError] = useState("");
  const [banner, setBanner] = useState("");
  const [headEditorOpen, setHeadEditorOpen] = useState(false);
  const [headDraft, setHeadDraft] = useState({ id: "", category: "", name: "", status: "Active", order: 1, columns: [], defaultRows: [""], description: "" });
  const [addTermFormOpen, setAddTermFormOpen] = useState(false);
  const [termDraft, setTermDraft] = useState({ id: "", title: "", text: "", order: 1, status: "Active" });
  const [expandedHeadIds, setExpandedHeadIds] = useState(() => heads.map((head) => head.id));
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [ruleSaving, setRuleSaving] = useState(false);
  const [ruleSavingId, setRuleSavingId] = useState(null);
  const [ruleDeletingId, setRuleDeletingId] = useState(null);
  const [headsLoading, setHeadsLoading] = useState(false);
  const [headsError, setHeadsError] = useState("");
  const [headSaving, setHeadSaving] = useState(false);
  const [headDeletingId, setHeadDeletingId] = useState(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [termSaving, setTermSaving] = useState(false);
  const [termDeletingId, setTermDeletingId] = useState(null);
  const [netPriceConfig, setNetPriceConfig] = useState({ id: "", tenantId: "", netPricePercentage: "" });
  const [netPriceLoading, setNetPriceLoading] = useState(false);
  const [netPriceSaving, setNetPriceSaving] = useState(false);
  const [netPriceError, setNetPriceError] = useState("");

  function showSuccess(message) {
    setBanner(message);
    window.setTimeout(() => setBanner(""), 2600);
  }

  function applyApprovalRulesFromResponse(data) {
    const nextRules = dedupeApprovalRules(extractApiApprovalRules(data));
    setRules(nextRules);
    return nextRules;
  }

  useEffect(() => {
    let active = true;
    setApproverOptionsLoading(true);
    setApproverOptionsError("");

    fetchRoles()
      .then((roles) => {
        if (!active) return;
        const nextOptions = roles
          .map((role) => String(role.name || "").trim())
          .filter(Boolean);
        setApproverOptions(Array.from(new Set(nextOptions)));
        if (!nextOptions.length) {
          setApproverOptionsError("No tenant roles were returned by the roles API.");
        }
      })
      .catch((error) => {
        if (!active) return;
        setApproverOptions([]);
        setApproverOptionsError(error.message || "Unable to load approval levels from the roles API.");
      })
      .finally(() => {
        if (!active) return;
        setApproverOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (activeSection !== "pmc") {
      return () => {
        active = false;
      };
    }

    async function loadPmcConfigs() {
      setHeadsLoading(true);
      setHeadsError("");

      try {
        const response = await getPmcConfigs();
        if (!active) return;
        const nextHeads = extractApiPmcConfigs(response?.data).map((head, index) => ({
          ...head,
          order: index + 1,
          dirty: false,
        }));
        setHeads(nextHeads);
        setExpandedHeadIds(nextHeads.map((head) => head.id));
      } catch (error) {
        if (!active) return;
        setHeadsError(error.message || "Failed to load PMC configurations.");
      } finally {
        if (active) setHeadsLoading(false);
      }
    }

    loadPmcConfigs();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "terms") {
      return () => {
        active = false;
      };
    }

    async function loadTermsConfig() {
      setTermsLoading(true);
      setTermsError("");

      try {
        const response = await getTerms();
        if (!active) return;
        setTerms(sortTermsForUi(extractApiTerms(response?.data)));
      } catch (error) {
        if (!active) return;
        setTermsError(error.message || "Failed to load standard terms.");
      } finally {
        if (active) setTermsLoading(false);
      }
    }

    loadTermsConfig();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "rules") {
      return () => {
        active = false;
      };
    }

    async function loadRuleConfigs() {
      setRulesLoading(true);
      setRulesError("");

      try {
        const rulesResponse = await getApprovalRules();
        if (!active) return;
        applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      } catch (error) {
        if (!active) return;
        setRulesError(error.message || "Failed to load sales configuration.");
      } finally {
        if (active) setRulesLoading(false);
      }
    }

    loadRuleConfigs();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "netPrice") {
      return () => {
        active = false;
      };
    }

    async function loadNetPriceConfig() {
      setNetPriceLoading(true);
      setNetPriceError("");

      try {
        const netPriceResponse = await getNetPriceRule();
        if (!active) return;
        const mapped = mapApiNetPriceRuleToUi(netPriceResponse?.data ?? netPriceResponse);
        setNetPriceConfig({
          id: mapped.id,
          tenantId: mapped.tenantId || String(sessionDB.getTenantId() || ""),
          netPricePercentage: mapped.netPricePercentage,
        });
      } catch (error) {
        if (!active) return;
        setNetPriceError(error.message || "Failed to load net price rule.");
      } finally {
        if (active) setNetPriceLoading(false);
      }
    }

    loadNetPriceConfig();
    return () => {
      active = false;
    };
  }, [activeSection]);

  async function moveRule(index, direction) {
    const sorted = sortApprovalRules(rules);
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= sorted.length) return;

    const next = [...sorted];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((item, itemIndex) => ({ ...item, displayOrder: itemIndex + 1 }));
    const changed = renumbered.filter((rule) => {
      const previous = sorted.find((item) => item.id === rule.id);
      return Number(previous?.displayOrder) !== Number(rule.displayOrder);
    });

    setRules(renumbered);
    if (!changed.length) return;

    setRuleSaving(true);
    setRulesError("");

    try {
      for (const rule of changed.filter((item) => !item.isDraft)) {
        await persistApprovalRule(rule, { previousId: rule.id });
      }
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      showSuccess("Approval rule order updated.");
    } catch (error) {
      setRulesError(error.message || "Failed to update approval rule order.");
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
    } finally {
      setRuleSaving(false);
    }
  }

  function patchRuleLocal(ruleId, updates) {
    setRules((current) =>
      current.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule))
    );
  }

  function addRuleRow() {
    setRules((current) => [
      ...current,
      createEmptyApprovalRule(current.length + 1, approverOptions),
    ]);
  }

  async function saveRuleInline(rule) {
    if (!rule) return;

    if (!rule.autoApprove && !String(rule.approvalLevel || "").trim()) {
      setRulesError("Select an approver for rules that need approval.");
      return;
    }

    const previousId = rule.isDraft ? 0 : Number(rule.id) || 0;

    setRuleSavingId(rule.id);
    setRuleSaving(true);
    setRulesError("");

    try {
      const savedRule = await persistApprovalRule(rule, { previousId });
      if (savedRule) {
        setRules((current) =>
          dedupeApprovalRules(
            sortApprovalRules([
              ...current.filter((item) => item.id !== rule.id && Number(item.id) !== previousId),
              savedRule,
            ])
          )
        );
      }
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      showSuccess(rule.isDraft ? "Approval rule saved." : "Approval rule updated.");
    } catch (error) {
      setRulesError(error.message || "Failed to save approval rule.");
    } finally {
      setRuleSavingId(null);
      setRuleSaving(false);
    }
  }

  function moveItem(list, setList, index, direction, orderKey = "order") {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    const swap = next[index];
    next[index] = next[target];
    next[target] = swap;
    setList(next.map((item, itemIndex) => ({ ...item, [orderKey]: itemIndex + 1 })));
    showSuccess("Configuration order updated.");
  }

  function openHeadEditor(head = null) {
    const presentation = buildPmcHeadPresentation(head?.name, head?.category);
    setHeadDraft(
      head
        ? {
            ...head,
            category: head.category || presentation.category,
            columns: Array.isArray(head.columns) ? [...head.columns] : [],
            defaultRows: Array.isArray(head.defaultRows) && head.defaultRows.length ? [...head.defaultRows] : [""],
            description: head.description || presentation.description,
          }
        : {
            id: "",
            category: "",
            name: "",
            status: "Active",
            order: heads.length + 1,
            columns: [],
            defaultRows: [""],
            description: "",
          }
    );
    setHeadEditorOpen(true);
  }

  async function refreshPmcHeads({ expandAll = false, savedRowsById = {} } = {}) {
    const response = await getPmcConfigs();
    const nextHeads = applySavedRowsToHeads(
      extractApiPmcConfigs(response?.data).map((head, index) => ({
        ...head,
        order: index + 1,
      })),
      savedRowsById
    );
    setHeads(nextHeads);
    setExpandedHeadIds((current) => {
      if (expandAll || !current.length) return nextHeads.map((head) => head.id);
      const retained = current.filter((id) => nextHeads.some((head) => head.id === id));
      return retained.length ? retained : nextHeads.map((head) => head.id);
    });
    return nextHeads;
  }

  function patchHeadLocal(headId, updater) {
    setHeads((current) =>
      current.map((head) => {
        if (head.id !== headId) return head;
        const next = typeof updater === "function" ? updater(head) : { ...head, ...updater };
        return { ...next, dirty: true };
      })
    );
  }

  function addBreakdownRow(headId) {
    patchHeadLocal(headId, (head) => ({
      ...head,
      defaultRows: [...(head.defaultRows || []), ""],
    }));
  }

  function updateBreakdownRow(headId, rowIndex, value) {
    patchHeadLocal(headId, (head) => ({
      ...head,
      defaultRows: (head.defaultRows || []).map((row, index) => (index === rowIndex ? value : row)),
    }));
  }

  function removeBreakdownRow(headId, rowIndex) {
    patchHeadLocal(headId, (head) => ({
      ...head,
      defaultRows: (head.defaultRows || []).filter((_, index) => index !== rowIndex),
    }));
  }

  function resolvePmcIdFromResponse(response, fallbackId = 0) {
    const payload =
      extractObjectPayload(response?.data) ||
      extractObjectPayload(response) ||
      null;
    return extractNumericId(payload?.id ?? payload?.pmcConfigId) || extractNumericId(fallbackId) || 0;
  }

  async function saveHead() {
    const presentation = buildPmcHeadPresentation(headDraft.name, headDraft.category);
    const normalized = {
      ...headDraft,
      category: headDraft.category || presentation.category,
      columns: (headDraft.columns || []).filter((column) => String(column || "").trim()),
      defaultRows: (headDraft.defaultRows || []).map((row) => String(row || "").trim()),
      description: headDraft.description || presentation.description,
      status: headDraft.status === "Inactive" ? "Inactive" : "Active",
    };

    if (!String(normalized.name || "").trim()) {
      setHeadsError("Cost head name is required.");
      return;
    }

    setHeadSaving(true);
    setHeadsError("");

    try {
      const payload = buildPmcConfigPayload(normalized);
      let savedId = Number(headDraft.id) || 0;
      let updateResponse = null;

      if (savedId > 0) {
        updateResponse = await updatePmcConfig(savedId, payload);
      } else {
        const createResponse = await createPmcConfig({ ...payload, id: 0 });
        savedId = resolvePmcIdFromResponse(createResponse);
        if (savedId > 0) {
          updateResponse = await updatePmcConfig(savedId, { ...payload, id: savedId });
        }
      }

      const responseRows = pickPmcRowsFromApi(
        extractObjectPayload(updateResponse?.data) || extractObjectPayload(updateResponse) || {}
      );
      const savedRows = responseRows.length
        ? responseRows
        : normalizePmcRowLabels(payload.defaultRows).filter(Boolean);

      await refreshPmcHeads({
        expandAll: true,
        savedRowsById: savedId > 0 ? { [String(savedId)]: savedRows } : {},
      });

      setHeadEditorOpen(false);
      showSuccess(Number(headDraft.id) > 0 ? "PMC cost configuration updated." : "PMC cost configuration saved.");
    } catch (error) {
      setHeadsError(error.message || "Failed to save PMC configuration.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function saveHeadInline(head) {
    if (!head?.id || headSaving) return;
    const numericId = Number(head.id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setHeadsError("Save the cost head first, then add rows.");
      return;
    }

    setHeadSaving(true);
    setHeadsError("");

    try {
      const payload = buildPmcConfigPayload(head);
      const updateResponse = await updatePmcConfig(numericId, payload);
      const responseRows = pickPmcRowsFromApi(
        extractObjectPayload(updateResponse?.data) || extractObjectPayload(updateResponse) || {}
      );
      const savedRows = responseRows.length
        ? responseRows
        : normalizePmcRowLabels(payload.defaultRows).filter(Boolean);

      await refreshPmcHeads({
        savedRowsById: { [String(numericId)]: savedRows },
      });
      showSuccess("PMC cost configuration updated.");
    } catch (error) {
      setHeadsError(error.message || "Failed to save PMC configuration.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function toggleHeadStatus(head) {
    if (!head?.id || headSaving) return;
    const numericId = Number(head.id);
    if (!Number.isFinite(numericId) || numericId <= 0) return;

    const nextStatus = head.status === "Active" ? "Inactive" : "Active";
    setHeadSaving(true);
    setHeadsError("");

    try {
      await updatePmcConfig(
        numericId,
        buildPmcConfigPayload({ ...head, status: nextStatus })
      );
      await refreshPmcHeads();
      showSuccess(nextStatus === "Active" ? "PMC cost head activated." : "PMC cost head deactivated.");
    } catch (error) {
      setHeadsError(error.message || "Failed to update PMC status.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function removeHead(id) {
    if (!id || headDeletingId) return;
    if (!window.confirm("Delete this PMC cost head? This cannot be undone.")) return;

    setHeadDeletingId(id);
    setHeadsError("");

    try {
      await deletePmcConfig(Number(id));
      setHeads((current) => current.filter((item) => item.id !== id).map((item, index) => ({ ...item, order: index + 1 })));
      setExpandedHeadIds((current) => current.filter((item) => item !== id));
      showSuccess("PMC cost configuration deleted.");
    } catch (error) {
      setHeadsError(error.message || "Failed to delete PMC configuration.");
    } finally {
      setHeadDeletingId(null);
    }
  }

  async function removeRule(id) {
    if (!id) return;

    if (String(id).startsWith("draft-")) {
      setRules((current) =>
        sortApprovalRules(current.filter((item) => item.id !== id))
      );
      return;
    }

    setRuleDeletingId(id);
    setRulesError("");

    try {
      await deleteApprovalRule(Number(id));
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      showSuccess("Approval rule deleted.");
    } catch (error) {
      setRulesError(error.message || "Failed to delete approval rule.");
    } finally {
      setRuleDeletingId(null);
    }
  }

  async function saveNetPriceConfig() {
    const payload = {
      id: Number(netPriceConfig.id) || 0,
      tenantId: String(netPriceConfig.tenantId || sessionDB.getTenantId() || ""),
      netPricePercentage: Number(netPriceConfig.netPricePercentage) || 0,
    };

    setNetPriceSaving(true);
    setNetPriceError("");

    try {
      const updateResponse = await updateNetPriceRule(payload);
      let mapped = mapApiNetPriceRuleToUi(updateResponse?.data ?? updateResponse);

      if (mapped.netPricePercentage === "" && !mapped.id) {
        const netPriceResponse = await getNetPriceRule();
        mapped = mapApiNetPriceRuleToUi(netPriceResponse?.data ?? netPriceResponse);
      }

      setNetPriceConfig({
        id: mapped.id || payload.id,
        tenantId: mapped.tenantId || payload.tenantId,
        netPricePercentage:
          mapped.netPricePercentage !== ""
            ? mapped.netPricePercentage
            : String(payload.netPricePercentage),
      });
      showSuccess(sq("netPriceRuleSaved"));
    } catch (error) {
      setNetPriceError(error.message || sq("netPriceRuleSaveFailed"));
    } finally {
      setNetPriceSaving(false);
    }
  }

  function openAddTermForm() {
    setTermDraft({ id: "", title: "New term", text: "", order: terms.length + 1, status: "Active" });
    setAddTermFormOpen(true);
  }

  function closeAddTermForm() {
    setAddTermFormOpen(false);
    setTermDraft({ id: "", title: "New term", text: "", order: terms.length + 1, status: "Active" });
  }

  function patchTermLocal(termId, updates) {
    setTerms((current) =>
      current.map((term) => (term.id === termId ? { ...term, ...updates } : term))
    );
  }

  function buildTermApiPayload(term) {
    return {
      id: extractNumericId(term.id) || 0,
      title: String(term.title || "").trim(),
      description: String(term.text || "").trim(),
      displayOrder: Number(term.order) || 1,
      isActive: term.status === "Active",
    };
  }

  async function refreshTermsList() {
    const response = await getTerms();
    const nextTerms = sortTermsForUi(extractApiTerms(response?.data));
    setTerms(nextTerms);
    return nextTerms;
  }

  async function saveTermInline(term) {
    if (!term?.id || termSaving) return;
    const numericId = extractNumericId(term.id);
    if (!numericId) return;

    const payload = buildTermApiPayload(term);
    if (!payload.title || !payload.description) {
      setTermsError("Title and term text are required.");
      return;
    }

    setTermSaving(true);
    setTermsError("");

    try {
      await updateTerm(numericId, payload);
      await refreshTermsList();
      showSuccess("Standard term saved.");
    } catch (error) {
      setTermsError(error.message || "Failed to save standard term.");
    } finally {
      setTermSaving(false);
    }
  }

  async function toggleTermStatus(term) {
    if (!term?.id || termSaving) return;
    const numericId = extractNumericId(term.id);
    if (!numericId) return;

    const nextStatus = term.status === "Active" ? "Inactive" : "Active";
    setTermSaving(true);
    setTermsError("");

    try {
      await updateTerm(numericId, {
        ...buildTermApiPayload(term),
        isActive: nextStatus === "Active",
      });
      await refreshTermsList();
      showSuccess(nextStatus === "Active" ? "Standard term activated." : "Standard term deactivated.");
    } catch (error) {
      setTermsError(error.message || "Failed to update standard term status.");
    } finally {
      setTermSaving(false);
    }
  }

  async function saveTerm(overrideDraft) {
    const source = overrideDraft || termDraft;
    const normalized = {
      ...source,
      title: String(source.title || "New term").trim(),
      text: String(source.text || "").trim(),
      order: Number(source.order) || terms.length + 1,
    };

    setTermSaving(true);
    setTermsError("");

    try {
      const payload = {
        id: extractNumericId(source.id) || 0,
        title: normalized.title,
        description: normalized.text,
        displayOrder: normalized.order,
        isActive: (source.status || "Active") === "Active",
      };

      const existingId = extractNumericId(source.id);
      if (existingId) {
        await updateTerm(existingId, payload);
      } else {
        await createTerm(payload);
      }

      await refreshTermsList();
      closeAddTermForm();
      showSuccess("Standard term saved.");
    } catch (error) {
      setTermsError(error.message || "Failed to save standard term.");
    } finally {
      setTermSaving(false);
    }
  }

  async function removeTerm(id) {
    const numericId = extractNumericId(id);
    if (!numericId) {
      setTermsError("Invalid term id.");
      return;
    }

    setTermDeletingId(id);
    setTermsError("");

    try {
      await deleteTerm(numericId);
      await refreshTermsList();
      showSuccess("Standard term deleted.");
    } catch (error) {
      setTermsError(error.message || "Failed to delete standard term.");
    } finally {
      setTermDeletingId(null);
    }
  }

  function toggleAccordion(id, setState) {
    setState((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  }

  function renderPmcPage() {
    return (
      <div className="space-y-3">
        <div>
          <h2 className={ui.pageTitle}>{sq("pmcCostHeadsTitle")}</h2>
        </div>

        {headsError ? (
          <div className="rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2.5 text-[12px] leading-5 text-[#b06c16]">
            <div className="flex items-start justify-between gap-3">
              <p>{headsError}</p>
              <button
                type="button"
                onClick={() => setHeadsError("")}
                className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#b06c16]"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {headsLoading ? (
          <TableSkeleton />
        ) : heads.length === 0 ? (
          <ConfigEmptyState
            title="No configured PMC heads"
            body="Add a PMC cost head to define the breakdown structure used in quotation Cost + Pricing."
            actionLabel="Add Cost Head"
            onAction={() => openHeadEditor()}
          />
        ) : (
          <div className="space-y-2.5">
            {heads.map((head, index) => {
              const open = expandedHeadIds.includes(head.id);
              const typeLabel = (head.category || inferPmcCategory(head.name) || head.name || "PMC").toUpperCase();
              const rows = Array.isArray(head.defaultRows) && head.defaultRows.length ? head.defaultRows : [""];
              return (
                <div
                  key={head.id}
                  className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white"
                >
                  <div className="flex items-center gap-2.5 px-3 py-2.5 sm:px-4">
                    <div className="flex shrink-0 flex-col items-center justify-center gap-0 leading-none text-[#90a2be]">
                      <button
                        type="button"
                        onClick={() => toggleAccordion(head.id, setExpandedHeadIds)}
                        className="flex h-3.5 w-4 items-center justify-center"
                        aria-label={open ? "Collapse" : "Expand"}
                      >
                        <MoveUp className={`h-3 w-3 transition ${open ? "" : "rotate-180"}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(heads, setHeads, index, "down")}
                        className="flex h-3.5 w-4 items-center justify-center"
                        aria-label="Move down"
                      >
                        <MoveDown className="h-3 w-3" />
                      </button>
                    </div>
                    <input
                      value={head.name}
                      onChange={(event) => patchHeadLocal(head.id, { name: event.target.value })}
                      placeholder="Cost head name"
                      className="min-w-0 flex-1 border-0 bg-transparent px-0 text-[15px] font-semibold text-[#23324c] outline-none placeholder:text-[#96a5bb] focus:border-b focus:border-[#9fb4dd]"
                      {...localizedFieldProps()}
                    />
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-[#eef2f8] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[#6b7f9d]">
                        {typeLabel}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleHeadStatus(head)}
                        disabled={headSaving || headDeletingId === head.id}
                        className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] transition ${
                          head.status === "Active"
                            ? "bg-[#dff8e8] text-[#219653] hover:bg-[#d1f3de]"
                            : "bg-[#eef2f8] text-[#7b8faa] hover:bg-[#e5ebf5]"
                        } disabled:opacity-60`}
                      >
                        {head.status}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeHead(head.id)}
                        disabled={headDeletingId === head.id || headSaving}
                        className="grid h-7 w-7 place-items-center rounded-md text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:opacity-60"
                        aria-label="Delete cost head"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {open && (
                    <div className="border-t border-[#e7edf7] px-3 py-2.5 sm:px-4">
                      <p className="mb-2 text-[11px] leading-5 text-[#8ea0bd]">
                        {head.description || buildPmcHeadPresentation(head.name, head.category).description}
                      </p>

                      <div className="space-y-1.5">
                        {rows.map((row, rowIndex) => (
                          <div key={`${head.id}-row-${rowIndex}`} className="flex items-center gap-2">
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-[#b8c4d8]" />
                            <input
                              value={row}
                              onChange={(event) => updateBreakdownRow(head.id, rowIndex, event.target.value)}
                              placeholder="Item"
                              className="h-9 min-h-0 flex-1 rounded-[8px] border border-[#d5deec] bg-white px-3 text-[13px] text-[#33425d] outline-none transition focus:border-[#9fb4dd]"
                              {...localizedFieldProps()}
                            />
                            <button
                              type="button"
                              onClick={() => removeBreakdownRow(head.id, rowIndex)}
                              className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8]"
                              aria-label="Delete row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                        <button
                          type="button"
                          onClick={() => addBreakdownRow(head.id)}
                          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#2944a8]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          <span>Add row</span>
                        </button>
                        <QuotationPrimaryButton
                          icon={Save}
                          onClick={() => saveHeadInline(head)}
                          disabled={headSaving || !String(head.name || "").trim() || !Number(head.id)}
                        >
                          {headSaving ? "Saving..." : head.dirty ? "Save changes" : "Save"}
                        </QuotationPrimaryButton>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={() => openHeadEditor()}
            disabled={headSaving}
            className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#2944a8] disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            <span>Add Cost Head</span>
          </button>
        </div>
      </div>
    );
  }

  function renderNetPricePage() {
    return (
      <div className="space-y-3">
        <div>
          <h2 className={ui.pageTitle}>{sq("netPriceConfiguration")}</h2>
        </div>

        {netPriceLoading ? (
          <TableSkeleton />
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
            <label className="w-full max-w-xs space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">{sq("netPricePercentage")}</span>
              <input
                value={netPriceConfig.netPricePercentage}
                onChange={(event) => setNetPriceConfig((current) => ({ ...current, netPricePercentage: event.target.value }))}
                placeholder="Enter percentage"
                inputMode="decimal"
                className="h-10 w-full rounded-[10px] border border-[#cfd9ea] bg-white px-3 text-[13px] text-[#23324c] outline-none transition focus:border-[#9fb4dd]"
                {...localizedFieldProps()}
              />
            </label>
            <button
              type="button"
              onClick={saveNetPriceConfig}
              disabled={netPriceSaving || netPriceLoading}
              className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-[10px] bg-[#243c93] px-4 text-[12px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6]"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{netPriceSaving ? sq("saving") : sq("save")}</span>
            </button>
          </div>
        )}
        {netPriceError ? (
          <p className="text-[12px] text-[#b35b62]">{netPriceError}</p>
        ) : null}
      </div>
    );
  }

  function renderRulesPage() {
    const sortedRules = sortApprovalRules(rules);
    const defaultApproverLabel = resolveDefaultApproverLabel(approverOptions);
    const ruleInputClass =
      "h-9 rounded-[10px] border border-[#d5deec] bg-white px-2 text-[10px] text-[#33425d] outline-none transition focus:border-[#9fb4dd] disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]";

    return (
      <div className="space-y-3">
        <div>
          <h2 className={ui.pageTitle}>{sq("approvalRulesTitle")}</h2>
        </div>

        {rulesLoading ? (
          <TableSkeleton />
        ) : sortedRules.length === 0 ? (
          <ConfigEmptyState
            title={sq("noApprovalRulesConfigured")}
            body={sq("approvalRulesEmptyBody")}
            actionLabel={sq("addRule")}
            onAction={addRuleRow}
          />
        ) : (
          <div className="space-y-2.5">
            <div className="hidden min-w-[980px] grid-cols-[88px_minmax(320px,1fr)_150px_180px_92px_88px] gap-3 px-4 text-[10px] font-bold uppercase tracking-[0.05em] text-[#96a7c2] lg:grid">
              <span>Order</span>
              <span>Condition</span>
              <span>Approval</span>
              <span>Route To</span>
              <span>Active</span>
              <span className="text-right">Actions</span>
            </div>

            {sortedRules.map((rule, index) => (
              <div
                key={rule.id || `rule-${index}`}
                className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white shadow-[0_10px_24px_-24px_rgba(30,51,131,0.18)]"
              >
                <div className="overflow-x-auto">
                  <div className="grid min-w-[980px] grid-cols-[88px_minmax(320px,1fr)_150px_180px_92px_88px] items-center gap-3 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="min-w-[20px] text-[15px] font-semibold text-[#8a9bb8]">{rule.displayOrder}</span>
                      <div className="flex flex-col text-[#98aac4]">
                        <button
                          type="button"
                          onClick={() => moveRule(index, "up")}
                          disabled={ruleSaving || rulesLoading || index === 0}
                          className="grid h-5 w-5 place-items-center rounded-md hover:bg-[#f1f5fb] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Move rule up"
                        >
                          <MoveUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveRule(index, "down")}
                          disabled={ruleSaving || rulesLoading || index === sortedRules.length - 1}
                          className="grid h-5 w-5 place-items-center rounded-md hover:bg-[#f1f5fb] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Move rule down"
                        >
                          <MoveDown className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-wrap items-center gap-1 text-[10px] text-[#5d7598]">
                      <span className="shrink-0">Selling ≥ ₹</span>
                      <input
                        value={rule.sellingPriceMin}
                        onChange={(event) => patchRuleLocal(rule.id, { sellingPriceMin: event.target.value })}
                        placeholder="any"
                        inputMode="decimal"
                        className={`${ruleInputClass} w-[72px] shrink-0 text-right`}
                        {...localizedFieldProps()}
                      />
                      <span className="shrink-0">· margin ≤</span>
                      <input
                        value={rule.marginMax}
                        onChange={(event) => patchRuleLocal(rule.id, { marginMax: event.target.value })}
                        placeholder="any"
                        inputMode="decimal"
                        className={`${ruleInputClass} w-[44px] shrink-0 text-right`}
                        {...localizedFieldProps()}
                      />
                      <span className="shrink-0">% · margin ≥</span>
                      <input
                        value={rule.marginMin}
                        onChange={(event) => patchRuleLocal(rule.id, { marginMin: event.target.value })}
                        placeholder="any"
                        inputMode="decimal"
                        className={`${ruleInputClass} w-[44px] shrink-0 text-right`}
                        {...localizedFieldProps()}
                      />
                      <span className="shrink-0">%</span>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => patchRuleLocal(rule.id, { autoApprove: !rule.autoApprove })}
                        disabled={ruleSaving || rulesLoading}
                        className={`inline-flex min-w-[132px] justify-center rounded-full px-3 py-2 text-[9px] font-bold uppercase tracking-[0.05em] transition disabled:cursor-not-allowed disabled:opacity-60 ${
                          rule.autoApprove ? "bg-[#eef2ff] text-[#2944a8]" : "bg-[#eef2f8] text-[#8a9bb8]"
                        }`}
                      >
                        {rule.autoApprove ? "Auto-approve" : "Needs approval"}
                      </button>
                    </div>

                    <div>
                      <select
                        value={rule.approvalLevel}
                        onChange={(event) => patchRuleLocal(rule.id, { approvalLevel: event.target.value })}
                        disabled={rule.autoApprove || approverOptionsLoading || approverOptions.length === 0}
                        className={`${ruleInputClass} w-full min-w-0 px-2.5 text-[11px] text-[#23324c]`}
                        {...localizedFieldProps()}
                      >
                        {!approverOptions.length ? (
                          <option value="">
                            {approverOptionsLoading ? "Loading approval levels..." : "No approval levels available"}
                          </option>
                        ) : null}
                        {approverOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => patchRuleLocal(rule.id, { isActive: !rule.isActive })}
                        disabled={ruleSaving || rulesLoading}
                        className={`inline-flex min-w-[56px] justify-center rounded-full px-2.5 py-2 text-[9px] font-bold uppercase tracking-[0.06em] transition disabled:cursor-not-allowed disabled:opacity-60 ${
                          rule.isActive ? "bg-[#dff8e8] text-[#219653]" : "bg-[#eef2f8] text-[#7b8faa]"
                        }`}
                      >
                        {rule.isActive ? "On" : "Off"}
                      </button>
                    </div>

                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => saveRuleInline(rule)}
                        disabled={ruleSavingId === rule.id || ruleSaving || rulesLoading}
                        className="grid h-8 w-8 place-items-center rounded-xl border border-[#dde5f1] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Save rule"
                      >
                        <Save className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRule(rule.id)}
                        disabled={ruleDeletingId === rule.id || ruleSaving || rulesLoading}
                        className="grid h-8 w-8 place-items-center rounded-xl border border-[#dde5f1] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Delete rule"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!rulesLoading && (
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={addRuleRow}
              disabled={ruleSaving || rulesLoading}
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              <span>{sq("addRule")}</span>
            </button>
            <div className="text-[12px] text-[#7184a3] sm:text-[13px]">
              <p>
                {sq("defaultApprover")}{" "}
                <span className="font-semibold text-[#5d7598]">{defaultApproverLabel}</span>
              </p>
              {approverOptionsError ? <p className="mt-1 text-[#b35b62]">{approverOptionsError}</p> : null}
              {rulesError ? <p className="mt-1 text-[#b35b62]">{rulesError}</p> : null}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderTermsPage() {
    const termTitleInputClass =
      "min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-[15px] font-semibold leading-tight text-[#23324c] outline-none placeholder:text-[#96a5bb] focus:border-b focus:border-[#cfd9ea]";
    const termStatusClass = (status) =>
      `shrink-0 rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] transition disabled:opacity-60 ${
        status === "Active"
          ? "bg-[#dff8e8] text-[#219653] hover:bg-[#d1f3de]"
          : "bg-[#eef2f8] text-[#7b8faa] hover:bg-[#e5ebf5]"
      }`;

    function TermReorderArrows({ onUp, onDown, disableUp, disableDown }) {
      return (
        <div className="flex shrink-0 flex-col items-center justify-center gap-0 leading-none text-[#90a2be] select-none">
          <button
            type="button"
            onClick={onUp}
            disabled={disableUp}
            className="flex h-3 w-4 items-center justify-center text-[12px] leading-none disabled:opacity-35"
            aria-label="Move up"
          >
            ⌃
          </button>
          <button
            type="button"
            onClick={onDown}
            disabled={disableDown}
            className="flex h-3 w-4 items-center justify-center text-[12px] leading-none disabled:opacity-35"
            aria-label="Move down"
          >
            ⌄
          </button>
        </div>
      );
    }

    function renderTermRow({
      title,
      text,
      status,
      onTitleChange,
      onTitleBlur,
      onTextChange,
      onTextBlur,
      onToggleStatus,
      onDelete,
      disableTitle = false,
      disableText = false,
      disableStatus = false,
      disableDelete = false,
      disableUp = false,
      disableDown = false,
      onUp,
      onDown,
    }) {
      return (
        <div className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white shadow-[0_10px_24px_-24px_rgba(30,51,131,0.18)]">
          <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4">
            <TermReorderArrows
              onUp={onUp}
              onDown={onDown}
              disableUp={disableUp}
              disableDown={disableDown}
            />
            <input
              value={title}
              onChange={onTitleChange}
              onBlur={onTitleBlur}
              disabled={disableTitle}
              className={termTitleInputClass}
              placeholder="Term title"
              {...localizedFieldProps()}
            />
            <button
              type="button"
              onClick={onToggleStatus}
              disabled={disableStatus}
              className={termStatusClass(status)}
            >
              {status}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={disableDelete}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Delete term"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="px-3 py-2.5 sm:px-4">
            <Textarea
              value={text}
              onChange={onTextChange}
              onBlur={onTextBlur}
              disabled={disableText}
              className="min-h-[52px] w-full resize-y rounded-[10px] border border-[#d5deec] bg-white px-3 py-2 text-[13px] leading-5 text-[#6b7f9d] outline-none transition focus:border-[#9fb4dd] disabled:bg-[#fbfcfe] disabled:text-[#8ea0bd]"
              {...localizedFieldProps()}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div>
          <h2 className={ui.pageTitle}>{sq("termsLibraryTitle")}</h2>
        </div>

        {termsError ? (
          <ErrorState message={termsError} onRetry={() => window.location.reload()} />
        ) : termsLoading ? (
          <TableSkeleton />
        ) : terms.length === 0 && !addTermFormOpen ? (
          <ConfigEmptyState
            title={sq("noStandardTermsConfigured")}
            body={sq("standardTermsEmptyBody")}
            actionLabel={sq("addTerm")}
            onAction={() => openAddTermForm()}
          />
        ) : (
          <>
            <div className="space-y-2.5">
              {terms.map((term, index) => {
                const label = term.title || term.text.split(/[.;]/)[0] || `Term ${index + 1}`;
                return (
                  <div key={term.id}>
                    {renderTermRow({
                      title: term.title || label,
                      text: term.text,
                      status: term.status,
                      onTitleChange: (event) => patchTermLocal(term.id, { title: event.target.value }),
                      onTitleBlur: (event) => saveTermInline({ ...term, title: event.target.value }),
                      onTextChange: (event) => patchTermLocal(term.id, { text: event.target.value }),
                      onTextBlur: (event) => saveTermInline({ ...term, text: event.target.value }),
                      onToggleStatus: () => toggleTermStatus(term),
                      onDelete: () => removeTerm(term.id),
                      disableTitle: termSaving || termDeletingId === term.id,
                      disableText: termSaving || termDeletingId === term.id,
                      disableStatus: termSaving || termDeletingId === term.id,
                      disableDelete: termDeletingId === term.id || termSaving,
                      disableUp: index === 0 || termSaving,
                      disableDown: index === terms.length - 1 || termSaving,
                      onUp: () => moveItem(terms, setTerms, index, "up"),
                      onDown: () => moveItem(terms, setTerms, index, "down"),
                    })}
                  </div>
                );
              })}

              {addTermFormOpen && (
                <div key="new-term">
                  {renderTermRow({
                    title: termDraft.title,
                    text: termDraft.text,
                    status: "Active",
                    onTitleChange: (event) => setTermDraft((current) => ({ ...current, title: event.target.value })),
                    onTextChange: (event) => setTermDraft((current) => ({ ...current, text: event.target.value })),
                    onTextBlur: (event) => {
                      const text = event.target.value;
                      if (!String(text).trim() || termSaving) return;
                      saveTerm({ ...termDraft, text });
                    },
                    onToggleStatus: () => undefined,
                    onDelete: closeAddTermForm,
                    disableTitle: termSaving,
                    disableText: termSaving,
                    disableStatus: true,
                    disableDelete: termSaving,
                    disableUp: true,
                    disableDown: true,
                    onUp: () => undefined,
                    onDown: () => undefined,
                  })}
                </div>
              )}
            </div>

            {!addTermFormOpen && (
              <div>
                <button
                  type="button"
                  onClick={() => openAddTermForm()}
                  className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#2944a8]"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add term</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {banner && (
        <div className="rounded-2xl border border-[#d5e8dc] bg-[#f4fff8] px-5 py-4 text-[#2b7a4b]">
          <p className="text-[13px] font-semibold">{banner}</p>
        </div>
      )}

      {activeSection === "pmc" && (
        <div className="min-w-0 rounded-[18px] border border-[#d9e2ef] bg-white px-4 py-5 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.16)] sm:px-5">
          {renderPmcPage()}
        </div>
      )}
      {activeSection === "rules" && renderRulesPage()}
      {activeSection === "netPrice" && renderNetPricePage()}
      {activeSection === "terms" && renderTermsPage()}

      <HeadEditorModal
        open={headEditorOpen}
        value={headDraft}
        onChange={setHeadDraft}
        onSave={saveHead}
        onCancel={() => setHeadEditorOpen(false)}
        saving={headSaving}
        hint={headDraft.id ? sq("pmcInlineEditHint") : sq("pmcCreateHeadHint")}
        title={headDraft.id ? "Edit PMC Cost Head" : "Add PMC Cost Head"}
      />
    </div>
  );
}

function QuotationWorkspace({ baseQuotation, baseHistory }) {
  useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("workingSheet");
  const [quotation, setQuotation] = useState(baseQuotation);
  const [workingRows, setWorkingRows] = useState(baseQuotation.workingRows);
  const [pmcEntries, setPmcEntries] = useState([]);
  const [hasPmcConfigs, setHasPmcConfigs] = useState(false);
  const [additionalTermEntries, setAdditionalTermEntries] = useState([]);
  const [netPricePercentage, setNetPricePercentage] = useState(0);
  const [standardTerms, setStandardTerms] = useState([]);
  const [selectedStandardTerms, setSelectedStandardTerms] = useState([]);
  const [termsPreviewOrder, setTermsPreviewOrder] = useState([]);
  const [overheadsPercent, setOverheadsPercent] = useState(String(baseQuotation.overheadsPct ?? "0"));
  const [markUpPercent, setMarkUpPercent] = useState(String(baseQuotation.markUpPct ?? "0"));
  const [statusNote, setStatusNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState(baseHistory);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const editable = quotationIsEditable(quotation?.status);

  useEffect(() => {
    setQuotation(baseQuotation);
    setWorkingRows(baseQuotation.workingRows);
    setOverheadsPercent(String(baseQuotation.overheadsPct ?? "0"));
    setMarkUpPercent(String(baseQuotation.markUpPct ?? "0"));
    setVersions(baseHistory);
    setActionError("");
    setSaveMessage("");
  }, [baseQuotation, baseHistory]);

  useEffect(() => {
    let active = true;

    resolvePmcEntriesForQuotation(baseQuotation.type, baseQuotation.pmcs || [])
      .then(({ entries, hasPmcConfigs: hasConfigs }) => {
        if (!active) return;
        setHasPmcConfigs(hasConfigs);
        setPmcEntries(entries);
      })
      .catch(() => {
        if (!active) return;
        setHasPmcConfigs(false);
        setPmcEntries([]);
      });

    return () => {
      active = false;
    };
  }, [baseQuotation.id, baseQuotation.type, baseQuotation.pmcs]);

  useEffect(() => {
    let active = true;

    Promise.allSettled([getNetPriceRule(), getTerms()])
      .then(([netPriceResult, termsResult]) => {
        if (!active) return;

        if (netPriceResult.status === "fulfilled") {
          const rawNetPrice = extractObjectPayload(netPriceResult.value?.data) || extractObjectPayload(netPriceResult.value) || {};
          const percentage = Number(rawNetPrice.netPricePercentage) || 0;
          setNetPricePercentage(percentage);
          if (percentage) {
            setWorkingRows((current) => applyNetPriceRuleToRows(current, percentage));
          }
        } else {
          setNetPricePercentage(0);
        }

        if (termsResult.status === "fulfilled") {
          const nextTerms = extractApiTerms(termsResult.value?.data)
            .sort((a, b) => Number(a.order) - Number(b.order))
            .map((term, index) => ({
              ...term,
              id: term.id ?? `term-${index + 1}`,
              description: term.text || term.description || "",
            }))
            .filter((term) => !term.status || term.status === "Active");
          const resolved = resolveTermsFromQuotation(baseQuotation.specificTerms || [], nextTerms);
          setStandardTerms(nextTerms);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setTermsPreviewOrder(resolved.previewOrder);
        } else {
          setStandardTerms([]);
          setSelectedStandardTerms([]);
          setAdditionalTermEntries([]);
          setTermsPreviewOrder([]);
        }
      });

    return () => {
      active = false;
    };
  }, [baseQuotation]);

  const showDownload = CUSTOMER_STAGE_STATUSES.includes(quotation.status);
  const showSubmit = quotationIsEditable(quotation.status);
  const showApprovedActions = quotation.status === "Approved";
  const showSentActions = quotation.status === "Sent to Customer";
  const showNegotiationActions = quotation.status === "Under Negotiation";
  const showRevisionNote = quotation.status === "Draft" && Boolean(quotation.revisionNote);
  const showSubmissionNote = quotation.status === "Pending Approval" && Boolean(quotation.submissionNote);

  function getTermsContext() {
    return {
      standardTerms,
      selectedStandardTerms,
      previewOrder: termsPreviewOrder,
      specificTerms: buildSpecificTermsPayload(
        standardTerms,
        selectedStandardTerms,
        additionalTermEntries,
        termsPreviewOrder
      ),
    };
  }

  async function persistWorkingSheet() {
    if (!quotation.id) return quotation;

    const rowsForSave = applyNetPriceRuleToRows(workingRows, netPricePercentage);
    setWorkingRows(rowsForSave);
    const termsContext = getTermsContext();
    const cleanedAdditionalTermEntries = additionalTermEntries
      .map((entry, index) => normalizeAdditionalTermEntry(entry, index))
      .filter((entry) => String(entry.description || "").trim() || String(entry.title || "").trim());

    await updateQuotationWorkingSheet(
      quotation.id,
      buildWorkingSheetPayload(
        quotation,
        rowsForSave,
        cleanedAdditionalTermEntries,
        pmcEntries,
        {
          overheadsPct: overheadsPercent,
          markUpPct: markUpPercent,
        },
        termsContext
      )
    );

    const next = {
      ...quotation,
      workingRows: rowsForSave,
      pmcs: pmcEntries,
      specificTerms: termsContext.specificTerms,
      overheadsPct: String(overheadsPercent),
      markUpPct: String(markUpPercent),
    };
    setAdditionalTermEntries(cleanedAdditionalTermEntries);
    setQuotation(next);
    return next;
  }

  async function saveWorkingSheetDraft() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      await persistWorkingSheet();
      setSaveMessage("Working sheet saved.");
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to save working sheet.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveTermsDraft() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      await persistWorkingSheet();
      setSaveMessage("Terms & conditions saved.");
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || "Failed to save terms & conditions.");
    } finally {
      setActionBusy(false);
    }
  }

  async function resolveQuotationId() {
    if (quotation.id) return quotation.id;

    const response = await getQuotations();
    const matched = extractApiCollection(response?.data)
      .map(normalizeApiQuotation)
      .find((item) => String(item.refId || "").trim().toUpperCase() === String(quotation.refId || "").trim().toUpperCase());

    if (!matched?.id) {
      throw new Error("Quotation was created, but its backend ID could not be resolved yet. Please refresh once and try again.");
    }

    setQuotation((current) => {
      return { ...current, id: matched.id };
    });

    return matched.id;
  }

  async function applyLatestQuotationState(latestQuotation) {
    setQuotation(latestQuotation);
    setWorkingRows(latestQuotation.workingRows || [buildBlankWorkingRow(latestQuotation.type)]);
    try {
      const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
        latestQuotation.type,
        latestQuotation.pmcs || []
      );
      setHasPmcConfigs(hasConfigs);
      setPmcEntries(entries);
    } catch {
      setHasPmcConfigs(false);
      setPmcEntries([]);
    }
    const resolved = resolveTermsFromQuotation(latestQuotation.specificTerms || [], standardTerms);
    setAdditionalTermEntries(resolved.additionalTermEntries);
    setSelectedStandardTerms(resolved.selectedIds);
    setTermsPreviewOrder(resolved.previewOrder);
    setOverheadsPercent(String(latestQuotation.overheadsPct ?? "0"));
    setMarkUpPercent(String(latestQuotation.markUpPct ?? "0"));
  }

  async function submitForApproval() {
    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      const validationMessage = validateWorkingRowsForSubmit(workingRows);
      if (validationMessage) {
        throw new Error(validationMessage);
      }

      await persistWorkingSheet();
      const quotationId = await resolveQuotationId();
      const submitResponse = await submitQuotation(quotationId);
      const submittedPayload =
        extractObjectPayload(submitResponse?.data) ||
        extractObjectPayload(submitResponse) ||
        null;

      let latestQuotation = submittedPayload ? normalizeApiQuotation(submittedPayload) : null;

      if (!latestQuotation?.refId || latestQuotation.status === "Draft") {
        const latestResponse = await getQuotationById(quotationId);
        if (!hasQuotationDetailPayload(latestResponse?.data) && !hasQuotationDetailPayload(latestResponse)) {
          throw new Error("Backend returned no quotation details for this record.");
        }
        latestQuotation = normalizeApiQuotation(latestResponse?.data || {});
      }

      if (!latestQuotation?.refId) {
        throw new Error("Quotation was submitted, but the updated backend state could not be confirmed.");
      }

      if (latestQuotation.status !== "Pending Approval" && latestQuotation.status !== "Approved") {
        throw new Error(
          `Quotation was submitted, but status is still "${latestQuotation.status}". Expected Pending Approval.`
        );
      }

      await applyLatestQuotationState(latestQuotation);
      setSaveMessage("");
    } catch (error) {
      setActionError(error.message || "Failed to submit quotation for approval.");
    } finally {
      setActionBusy(false);
    }
  }

  async function createNewVersion() {
    if (!window.confirm("Create a new version from the latest quotation?")) return;

    setActionBusy(true);
    setActionError("");

    try {
      await persistWorkingSheet();

      if (quotation.id) {
        const response = await reviseQuotation(quotation.id);
        let nextVersion = response?.data && typeof response.data === "object" && !Array.isArray(response.data)
          ? normalizeApiQuotation(response.data)
          : null;

        if (!nextVersion?.refId) {
          const latestListResponse = await getQuotations();
          nextVersion = extractApiCollection(latestListResponse?.data)
            .map(normalizeApiQuotation)
            .filter((item) => String(item.refId || "").trim().toUpperCase() === String(quotation.refId || "").trim().toUpperCase())
            .sort((a, b) => Number(b.version) - Number(a.version))[0] || null;
        }

        if (!nextVersion?.refId) {
          throw new Error("Backend did not return the revised quotation version.");
        }
        const nextHistory = [nextVersion, ...versions.map((item) => ({ ...item, superseded: true }))];

        navigate(`/sales/quotations/quotation/${nextVersion.id ?? nextVersion.refId}`, {
          state: {
            quotation: nextVersion,
            versionHistory: nextHistory,
          },
        });
        return;
      }
      throw new Error("Quotation revision requires a backend quotation ID. Please refresh and try again.");
    } catch (error) {
      setActionError(error.message || "Failed to create a new revision.");
    } finally {
      setActionBusy(false);
    }
  }

  async function applyCustomerStageStatus({ apiStatus, note, expectedUiStatus, successMessage }) {
    if (!quotation.id) {
      setActionError("This quotation does not have a backend ID yet, so status update is unavailable.");
      return;
    }

    setActionBusy(true);
    setActionError("");
    setSaveMessage("");

    try {
      const statusResponse = await updateQuotationStatus(quotation.id, {
        status: apiStatus,
        note,
      });
      const statusPayload =
        extractObjectPayload(statusResponse?.data) ||
        extractObjectPayload(statusResponse) ||
        null;

      let next = statusPayload ? normalizeApiQuotation(statusPayload) : null;
      if (!next?.refId || next.status !== expectedUiStatus) {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation(latest?.data || {});
      }

      if (!next?.refId) {
        throw new Error("Backend did not return the updated quotation after status change.");
      }
      if (next.status !== expectedUiStatus) {
        throw new Error(
          `Status update completed, but status is still "${next.status}". Expected ${expectedUiStatus}.`
        );
      }

      await applyLatestQuotationState(next);
      setStatusNote("");
      setSaveMessage(successMessage);
      window.setTimeout(() => setSaveMessage(""), 2600);
    } catch (error) {
      setActionError(error.message || `Failed to update quotation status to ${expectedUiStatus}.`);
    } finally {
      setActionBusy(false);
    }
  }

  async function markAsSentToCustomer() {
    await applyCustomerStageStatus({
      apiStatus: "SENT_TO_CUSTOMER",
      note: "Sent_To_Customer",
      expectedUiStatus: "Sent to Customer",
      successMessage: "Quotation marked as Sent to Customer.",
    });
  }

  async function markUnderNegotiation() {
    await applyCustomerStageStatus({
      apiStatus: "UNDER_NEGOTIATION",
      note: "under_negoiattion",
      expectedUiStatus: "Under Negotiation",
      successMessage: "Quotation marked as Under Negotiation.",
    });
  }

  async function markAccepted() {
    await applyCustomerStageStatus({
      apiStatus: "ACCEPTED",
      note: "accepted",
      expectedUiStatus: "Accepted",
      successMessage: "Quotation marked as Accepted.",
    });
  }

  async function markRejected() {
    await applyCustomerStageStatus({
      apiStatus: "REJECTED",
      note: "rejected",
      expectedUiStatus: "Rejected",
      successMessage: "Quotation marked as Rejected.",
    });
  }

  async function handleDownloadPdf() {
    if (!quotation.id) {
      setActionError("This quotation does not have a backend ID yet, so PDF download is unavailable.");
      return;
    }

    setActionBusy(true);
    setActionError("");

    try {
      const result = await downloadQuotationPdf(quotation.id);
      if (result?.blob) {
        triggerBrowserDownload(
          result.blob,
          parseContentDispositionFilename(result.filename, `${quotation.refId || "quotation"}.pdf`)
        );
      }
    } catch (error) {
      setActionError(error.message || "Failed to download quotation PDF.");
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        className="inline-flex items-center gap-2 text-[13px] font-medium text-[#5d7598] transition hover:text-[#2944a8]"
        onClick={() => navigate("/sales/quotations/quotation")}
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <div className="rounded-[12px] border border-[#d8e0ed] bg-white px-4 py-3 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] sm:px-5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[20px] font-semibold tracking-[0.01em] text-[#23324c]">{quotation.refId}</h2>
              <StatusPill tone={quotation.statusTone} label={quotation.status} />
              <span className="rounded-full bg-[#eef2f8] px-2.5 py-0.5 text-[11px] font-semibold text-[#647b9f]">{quotation.versionLabel}</span>
              {quotation.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
            </div>
            <p className="max-w-4xl text-[13px] leading-5 text-[#7384a2]">
              {quotation.customer} · {translateQuotationType(quotation.type)} · {quotation.location}
            </p>
            {showSubmissionNote && (
              <div className="max-w-3xl rounded-[10px] border border-[#d5e8dc] bg-[#f4fff8] px-3 py-2 text-[12px] leading-5 text-[#2b7a4b]">
                <p className="mt-0">{quotation.submissionNote}</p>
              </div>
            )}
            {showRevisionNote && (
              <div className="max-w-3xl rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2 text-[12px] leading-5 text-[#b06c16]">
                <p className="font-semibold">Returned for revision</p>
                <p className="mt-0.5">{quotation.revisionNote}</p>
              </div>
            )}
          </div>

          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 lg:shrink-0">
            {showSubmit && (
              <QuotationPrimaryButton icon={Send} onClick={submitForApproval} disabled={actionBusy}>
                {actionBusy ? sq("submitting") : sq("submitForApproval")}
              </QuotationPrimaryButton>
            )}
            <QuotationSecondaryButton icon={Sparkles} onClick={createNewVersion} disabled={actionBusy}>
              {sq("createNewVersion")}
            </QuotationSecondaryButton>
            <QuotationSecondaryButton icon={Clock3} onClick={() => setHistoryOpen(true)}>
              {sq("versionHistory")}
            </QuotationSecondaryButton>
            {showDownload && (
              <QuotationSecondaryButton icon={FileText} onClick={handleDownloadPdf} disabled={actionBusy}>
                {sq("downloadPdf")}
              </QuotationSecondaryButton>
            )}
          </div>
        </div>

        {actionError && <div className="mt-2.5"><ErrorState message={actionError} onRetry={() => setActionError("")} /></div>}
        {saveMessage ? (
          <div className="mt-2.5 rounded-[10px] border border-[#d5e8dc] bg-[#f4fff8] px-3 py-2 text-[#2b7a4b]">
            <p className="text-[12px] font-semibold">{saveMessage}</p>
          </div>
        ) : null}

        {(showApprovedActions || showSentActions || showNegotiationActions) && (
          <div className="mt-2.5 rounded-[10px] border border-[#e1e7f2] bg-[#fbfcff] px-3 py-2.5">
            <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap gap-2">
                {showApprovedActions && (
                  <QuotationPrimaryButton icon={Send} onClick={markAsSentToCustomer} disabled={actionBusy}>
                    Mark as Sent to Customer
                  </QuotationPrimaryButton>
                )}
                {showSentActions && (
                  <>
                    <QuotationSecondaryButton icon={Clock3} onClick={markUnderNegotiation} disabled={actionBusy}>
                      Mark Under Negotiation
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                      Mark Accepted
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                      Mark Rejected
                    </QuotationSecondaryButton>
                  </>
                )}
                {showNegotiationActions && (
                  <>
                    <QuotationSecondaryButton icon={Check} onClick={markAccepted} disabled={actionBusy}>
                      Mark Accepted
                    </QuotationSecondaryButton>
                    <QuotationSecondaryButton icon={XCircle} onClick={markRejected} disabled={actionBusy}>
                      Mark Rejected
                    </QuotationSecondaryButton>
                  </>
                )}
              </div>
              <div className="flex min-w-[240px] items-center gap-2">
                <input
                  value={statusNote}
                  onChange={(event) => setStatusNote(event.target.value)}
                  placeholder="Optional note for this status update"
                  className="min-h-[36px] w-full rounded-[10px] border border-[#d1d9e8] bg-white px-3 text-[12px] text-[#2d394c] outline-none placeholder:text-[#96a5bb]"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <SectionShell
        compact
        title={sq("customerAndDetails")}
        action={editable ? <span className="text-[11px] font-semibold text-[#1f9b56]">{sq("editable")}</span> : <div className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#9baac0]"><Lock className="h-3.5 w-3.5" /><span>{sq("locked")}</span></div>}
      >
        <div className="grid gap-3 px-4 py-3.5 sm:grid-cols-2 sm:gap-x-5 sm:px-5 lg:grid-cols-3">
          {[
            { key: "customer", label: sq("customerName"), type: "text" },
            { key: "location", label: sq("location"), type: "text" },
            { key: "pointOfContact", label: sq("pointOfContact"), type: "text" },
            { key: "phone", label: sq("phoneNumber"), type: "tel" },
            { key: "email", label: sq("email"), type: "email" },
            { key: "salesExecutive", label: sq("salesExecutive"), type: "text" },
            { key: "salesSupport", label: sq("salesSupport"), type: "text" },
            { key: "tenderDue", label: sq("tenderDue"), type: "date" },
            { key: "expiry", label: sq("expiry"), type: "date" },
          ].map((field) => (
            <div key={field.key}>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-[#96a7c2]">{field.label}</p>
              <input
                type={field.type}
                value={field.type === "date" ? String(quotation[field.key] || "").slice(0, 10) : (quotation[field.key] || "")}
                disabled={!editable}
                onChange={(event) => {
                  const value = event.target.value;
                  setQuotation((current) => hydrateQuotation({ ...current, [field.key]: value }));
                }}
                className={`${filterControlClass()} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                {...localizedFieldProps()}
              />
            </div>
          ))}
        </div>
      </SectionShell>

      <div className="flex flex-wrap items-end gap-5 border-b border-[#d7dfec] px-1">
          {[
            { key: "workingSheet", label: sq("workingSheet") },
            { key: "costPricing", label: sq("costPricing"), badge: sq("internal") },
            { key: "terms", label: sq("termsConditions") },
          ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`relative pb-3 text-[13px] font-semibold transition ${activeTab === tab.key ? "text-[#2944a8]" : "text-[#6f82a1]"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span>{tab.label}</span>
            {tab.badge && <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#ef8c16]">{tab.badge}</span>}
            {activeTab === tab.key && <span className="absolute inset-x-0 bottom-[-1px] h-[2px] rounded-full bg-[#2944a8]" />}
          </button>
        ))}
      </div>

      {activeTab === "workingSheet" && (
        <WorkingSheetTab
          quotationType={quotation.type}
          rows={workingRows}
          setRows={setWorkingRows}
          editable={editable}
          onSave={editable ? saveWorkingSheetDraft : undefined}
          saving={actionBusy}
        />
      )}
      {activeTab === "costPricing" && (
        <CostPricingTab
          quotationType={quotation.type}
          rows={workingRows}
          setRows={setWorkingRows}
          editable={editable}
          pmcEntries={pmcEntries}
          setPmcEntries={setPmcEntries}
          hasPmcConfigs={hasPmcConfigs}
          overheadsPercent={overheadsPercent}
          setOverheadsPercent={setOverheadsPercent}
          markUpPercent={markUpPercent}
          setMarkUpPercent={setMarkUpPercent}
          netPricePercentage={netPricePercentage}
        />
      )}
      {activeTab === "terms" && (
        <TermsAndConditionsTab
          standardTerms={standardTerms}
          selectedStandardTerms={selectedStandardTerms}
          setSelectedStandardTerms={setSelectedStandardTerms}
          additionalTermEntries={additionalTermEntries}
          setAdditionalTermEntries={setAdditionalTermEntries}
          previewOrder={termsPreviewOrder}
          setPreviewOrder={setTermsPreviewOrder}
          editable={editable}
          onSave={editable ? saveTermsDraft : undefined}
          saving={actionBusy}
        />
      )}

      <LifecycleStepper status={quotation.status} />

      <VersionHistoryDrawer open={historyOpen} versions={versions} currentVersion={quotation.version} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}

export function PmcCostHeadsPage() {
  useTranslation();
  const { pathname } = useLocation();
  const activeSection = pathname.includes("/approval-rules")
    ? "rules"
    : pathname.includes("/net-price-configuration")
      ? "netPrice"
    : pathname.includes("/terms-library")
      ? "terms"
      : "pmc";

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl">
        <PageBackLink to="/" />
        <ContentCard className="border-transparent bg-transparent shadow-none" bodyClassName="p-0">
          <QuotationConfigurationWorkspace activeSection={activeSection} />
        </ContentCard>
      </div>
    </AppLayout>
  );
}

export function QuotationPage() {
  useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
 
  useEffect(() => {
    let active = true;

    async function loadQuotations() {
      setLoading(true);
      setLoadError("");

      try {
        const response = await getQuotations();
        const items = extractApiCollection(response?.data).map(normalizeApiQuotation);
        if (!active) return;
        setQuotations(items);
      } catch (error) {
        if (!active) return;
        setLoadError(error.message || sq("errorTitle"));
        setQuotations([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadQuotations();
    return () => {
      active = false;
    };
  }, []);

  const latestQuotations = useMemo(() => quotations, [quotations]);

  const filteredQuotations = useMemo(() => {
    return latestQuotations.filter((item) => {
      const query = search.trim().toLowerCase();
      const matchesSearch = !query || item.refId.toLowerCase().includes(query) || item.customer.toLowerCase().includes(query);
      const matchesStatus = !statusFilter || item.status === statusFilter;
      const matchesType = !typeFilter || item.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [latestQuotations, search, statusFilter, typeFilter]);

  function clearFilters() {
    setSearch("");
    setStatusFilter("");
    setTypeFilter("");
  }

  function retryLoad() {
    setQuotations([]);
    setLoading(true);
    setLoadError("");
    getQuotations()
      .then((response) => {
        const items = extractApiCollection(response?.data).map(normalizeApiQuotation);
        setQuotations(items);
      })
      .catch((error) => {
        setLoadError(error.message || sq("errorTitle"));
        setQuotations([]);
      })
      .finally(() => setLoading(false));
  }

  function navigateToQuotation(row) {
    navigate(`/sales/quotations/quotation/${row.id ?? row.refId}`, {
      state: {
        quotation: row,
        versionHistory: [row],
      },
    });
  }

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageBackLink to="/" />
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className={ui.pageTitle}>{sq("allQuotations")}</h2>
          </div>
          <QuotationPrimaryButton onClick={() => navigate("/sales/quotations/quotation/create")}>
            {sq("createQuotation")}
          </QuotationPrimaryButton>
        </div>

        {loadError && <ErrorState message={loadError} onRetry={retryLoad} />}

        <ContentCard className="overflow-hidden rounded-[14px] border border-[#d3dceb] bg-white shadow-[0_12px_28px_-24px_rgba(30,51,131,0.22)]" bodyClassName="p-0">
          <div className="flex flex-col gap-2 border-b border-[#e1e8f2] px-3 py-2.5 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid w-full gap-2 lg:min-w-0 lg:flex-1 lg:grid-cols-[1.45fr_0.82fr_0.82fr]">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] leading-none text-[#9aacbf]">⌕</span>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={sq("searchPlaceholder")}
                  className={`${filterControlClass()} pl-9`}
                />
              </div>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className={filterControlClass()}>
                <option value="">{sq("allStatuses")}</option>
                {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{translateQuotationStatus(status)}</option>)}
              </select>
              <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className={filterControlClass()}>
                <option value="">{sq("allTypes")}</option>
                {SALES_TYPES.map((type) => <option key={type} value={type}>{translateQuotationType(type)}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between gap-2 lg:min-w-[160px] lg:justify-end">
              <span className="text-[12px] text-[#8ea0bd]">{sq("countLabel", { shown: filteredQuotations.length, total: latestQuotations.length })}</span>
              {(search || statusFilter || typeFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="h-9 rounded-[10px] border border-[#d1d9e8] px-2.5 text-[11px] font-semibold text-[#5d7598] transition hover:border-[#9eb3dc] hover:text-[#2944a8]"
                >
                  {sq("reset")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <TableSkeleton />
          ) : latestQuotations.length === 0 ? (
            <EmptyState onCreate={() => navigate("/sales/quotations/quotation/create")} />
          ) : filteredQuotations.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <p className="text-[14px] font-semibold text-[#23324c]">{sq("noFilterMatch")}</p>
              <p className="mt-1.5 text-[12px] text-[#7184a3]">{sq("noFilterMatchHint")}</p>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr className="border-b border-[#d7dfec] text-left">
                    {[sq("refId"), sq("customerName"), sq("quotationType"), sq("version"), sq("status"), sq("sellingPrice"), sq("tenderDueDate"), sq("expiryDate"), sq("lastUpdated")].map((heading) => (
                      <th key={heading} className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.04em] text-[#96a7c2]">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                   {filteredQuotations.map((row) => (
                     <tr
                       key={`${row.refId}-${row.version}`}
                       className="cursor-pointer border-b border-[#e1e8f2] transition hover:bg-[#f8fbff] last:border-b-0"
                       onClick={() => navigateToQuotation(row)}
                     >
                      <td className="px-3 py-2 text-[12px] font-medium text-[#2f4da2]">{row.refId}</td>
                      <td className="px-3 py-2 text-[12px] font-semibold leading-5 text-[#2d394c]">
                        <span className="block max-w-[200px] truncate">{row.customer}</span>
                      </td>
                       <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{translateQuotationType(row.type)}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.versionLabel}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          <StatusPill tone={row.statusTone} label={row.status} />
                          {row.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[12px] font-semibold text-[#2d394c]">{row.sellingPrice}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.tenderDueLabel}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.expiryLabel}</td>
                      <td className="px-3 py-2 text-[12px] text-[#9aacbf]">{row.updatedLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — CRM Customer Onboarding style */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
              {filteredQuotations.map((row) => (
                <div
                  key={`mobile-${row.refId}-${row.version}`}
                  className="p-4 space-y-3 active:bg-[var(--color-workspace)]/40 transition cursor-pointer"
                  onClick={() => navigateToQuotation(row)}
                >
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-semibold text-foreground flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-[var(--color-primary)] shrink-0" />
                      <span className="break-words">{row.refId}</span>
                    </h4>
                    <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                      {translateQuotationType(row.type)}
                    </span>
                  </div>

                  <div className="text-xs space-y-1.5 text-[var(--color-text-secondary)]">
                    <div className="flex justify-between gap-3">
                      <span>{sq("customerName")}</span>
                      <span className="font-medium text-foreground text-right break-words">{row.customer || "—"}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("version")}</span>
                      <span className="font-medium text-foreground">{row.versionLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("sellingPrice")}</span>
                      <span className="font-medium text-foreground">{row.sellingPrice}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("tenderDueDate")}</span>
                      <span className="font-medium text-foreground">{row.tenderDueLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("expiryDate")}</span>
                      <span className="font-medium text-foreground">{row.expiryLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("lastUpdated")}</span>
                      <span className="font-medium text-foreground">{row.updatedLabel}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 pt-2">
                    <StatusPill tone={row.statusTone} label={row.status} />
                    {row.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </ContentCard>
      </div>
    </AppLayout>
  );
}

export function QuotationDetailPage() {
  useTranslation();
  const { refId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(refId !== "create" && !location.state?.quotation);
  const [loadError, setLoadError] = useState("");
  const [remoteQuotation, setRemoteQuotation] = useState(null);
  const [remoteHistory, setRemoteHistory] = useState(null);

  const incomingQuotation = useMemo(
    () => (location.state?.quotation ? hydrateQuotation(location.state.quotation) : null),
    [location.state?.quotation]
  );
  const incomingHistory = useMemo(
    () => (location.state?.versionHistory?.map(hydrateQuotation) || null),
    [location.state?.versionHistory]
  );

  useEffect(() => {
    let active = true;

    if (refId === "create") {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    if (incomingQuotation) {
      setRemoteQuotation(incomingQuotation);
      setRemoteHistory(incomingHistory || [incomingQuotation]);
    }

    const numericId = extractNumericId(refId) || extractNumericId(incomingQuotation?.id);
    if (!numericId) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setLoadError("");

    getQuotationById(numericId)
      .then((response) => {
        if (!active) return;
        if (!hasQuotationDetailPayload(response?.data) && !hasQuotationDetailPayload(response)) {
          setRemoteQuotation(null);
          setRemoteHistory(null);
          setLoadError("Quotation details were not returned by the backend for this ID.");
          return;
        }
        const normalized = normalizeApiQuotation(response?.data || {});
        setRemoteQuotation(normalized);
        setRemoteHistory([normalized]);
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(error.message || "Failed to load quotation.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [refId, incomingQuotation, incomingHistory]);

  if (refId === "create" && !incomingQuotation) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-[1280px]">
          <CreateQuotationForm />
        </div>
      </AppLayout>
    );
  }

  if (!loading && !remoteQuotation && !incomingQuotation) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-[1320px]">
          <ErrorState message={loadError || "Quotation not found."} onRetry={() => window.location.reload()} />
        </div>
      </AppLayout>
    );
  }

  const baseQuotation = remoteQuotation || incomingQuotation;
  const baseHistory = remoteHistory || incomingHistory || [baseQuotation];
  const detailContent = String(baseQuotation?.status || "").trim().toLowerCase() === "pending approval" ? (
    <ManagerQuotationReview baseQuotation={baseQuotation} baseHistory={baseHistory} />
  ) : (
    <QuotationWorkspace baseQuotation={baseQuotation} baseHistory={baseHistory} />
  );

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-[1320px]">
        {loadError && <ErrorState message={loadError} onRetry={() => window.location.reload()} />}
        {loading ? (
          <ContentCard className="overflow-hidden rounded-[18px] border border-[#d3dceb] bg-white shadow-[0_12px_28px_-24px_rgba(30,51,131,0.22)]" bodyClassName="p-0">
            <TableSkeleton />
          </ContentCard>
        ) : (
          detailContent
        )}
      </div>
    </AppLayout>
  );
}

